import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  SelectChangeEvent,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
// import SaveIcon from '@mui/icons-material/Save';
import { createLoanApplicationWithAttachments, createLoanApplicationJson, uploadLoanAttachmentsBatched, updateLoanApplication } from '../api/loanApplication';
import { collectDocumentUploadItems, emptyLoanAttachments, LOAN_DOCUMENT_SLOTS, type LoanAttachmentsState } from '../constants/loanDocumentTypes';
import { productApi, Product } from '../api/admin';
import {
  isApplicantEligibleForProduct,
  productGenderRestrictionMessage,
  resolveProductEligibleGender,
  filterProductsForApplicantGender,
  getProductTenureOptions,
} from '../utils/productEligibility';
import { formDraftService } from '../services/FormDraftService';
import { useSessionManager } from '../hooks/useSessionManager';
import { useDecodedToken } from '../utils/useDecodedToken';
import { isAuthenticated } from '../utils/auth';
import { isValidZimbabweNationalId } from '../utils/normalization';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';
import LabelWithHelp from '../components/loans/LabelWithHelp';
import FieldHelpIcon from '../components/loans/FieldHelpIcon';
import { FIELD_HELP } from '../components/loans/fieldHelpContent';

/** Map Skyfin/local currency codes onto the form's USD | ZIG values. */
const normalizeLoanCurrency = (raw?: string | null): 'USD' | 'ZIG' | '' => {
  if (!raw) return '';
  const code = String(raw).trim().toUpperCase();
  if (code === 'USD' || code === 'US$') return 'USD';
  // Zimbabwe gold / ZiG variants used in Skyfin and locally
  if (code === 'ZIG' || code === 'ZWG' || code === 'ZWL' || code === 'ZW$' || code === 'Z$') return 'ZIG';
  return '';
};

// Shared list of nationalities for the nationality dropdown (alphabetical, not exhaustive but broad)
const NATIONALITIES: string[] = [
  'Afghan',
  'Albanian',
  'Algerian',
  'American',
  'Angolan',
  'Argentinian',
  'Australian',
  'Austrian',
  'Bangladeshi',
  'Belgian',
  'Botswanan',
  'Brazilian',
  'British',
  'Bulgarian',
  'Cameroonian',
  'Canadian',
  'Chinese',
  'Colombian',
  'Congolese',
  'Croatian',
  'Czech',
  'Danish',
  'Dutch',
  'Egyptian',
  'Emirati',
  'Ethiopian',
  'Finnish',
  'French',
  'German',
  'Ghanaian',
  'Greek',
  'Hungarian',
  'Indian',
  'Indonesian',
  'Iranian',
  'Iraqi',
  'Irish',
  'Israeli',
  'Italian',
  'Ivorian',
  'Japanese',
  'Jordanian',
  'Kenyan',
  'Kuwaiti',
  'Lebanese',
  'Lesotho',
  'Liberian',
  'Libyan',
  'Malawian',
  'Malaysian',
  'Malian',
  'Mauritian',
  'Mexican',
  'Moroccan',
  'Mozambican',
  'Namibian',
  'Nigerian',
  'Norwegian',
  'Pakistani',
  'Polish',
  'Portuguese',
  'Qatari',
  'Romanian',
  'Russian',
  'Rwandan',
  'Saudi Arabian',
  'Scottish',
  'Senegalese',
  'Sierra Leonean',
  'Singaporean',
  'Somali',
  'South African',
  'South Korean',
  'Spanish',
  'Sri Lankan',
  'Sudanese',
  'Swazi',
  'Swedish',
  'Swiss',
  'Syrian',
  'Tanzanian',
  'Thai',
  'Tunisian',
  'Turkish',
  'Ugandan',
  'Ukrainian',
  'Uruguayan',
  'Venezuelan',
  'Vietnamese',
  'Zambian',
  'Zimbabwean',
  'Other'
].sort();

// Exact model per spec
interface TradeReferenceDto { tradeName: string; accountNumber: string; phoneNumber: string }
interface BankingInfoDto { bankName: string; branch: string; accountType: string; accountNumber: string }
interface LoanOrDebtDto { toWhom: string; amountOwed: string | number; purpose: string; monthlyRepayment: string | number }
interface OtherBusiness { nameOfBusiness: string; natureOfOperation: string; location: string }

interface BusinessInfoDto {
  nameOfApplicant: string;
  nameOfBusiness: string;
  statusOfBusiness: 'New' | 'Operating' | '';
  formOfBusinessOwnership: 'Sole Proprietorship' | 'Company' | 'Co-operative' | 'Partnership' | 'Other' | '';
  tradeReferenceDto: TradeReferenceDto[];
  bankingInfoDto: BankingInfoDto[];
  loanOrDebtDto: LoanOrDebtDto[];
  otherBusinesses: OtherBusiness[];
  certifierName?: string;
  positionInBusiness?: string;
  signature?: string;
  certificationDate?: string; // YYYY-MM-DD
}

interface LoanDetailsDto {
  loanAmount?: number;
  loanPurpose: string;
  currency: 'ZIG' | 'USD' | '';
  loanProduct: string; // Now accepts any string (product name from database)
  tenure: string;
  branchName?: string;
}

interface AddressDto { 
  streetAddress: string; 
  city: string; 
  state?: string; 
  postalCode?: string; 
  country: string;
  lengthOfStay?: string;
  ownershipOfProperty?: string;
}
interface ContactDetails { 
  phoneNumber: string; 
  emailAddress: string; 
  alternativePhone?: string;
  postalAddress?: string;
  homePhone?: string;
  businessPhone?: string;
}
interface NextOfKin { name: string; phone: string; relationship: string; address: string }
interface Qualification { institution: string; qualification: string; startDate: string; endDate: string }
interface EmploymentHistory { employer: string; jobTitle: string; startDate: string; endDate: string }

interface PersonDto {
  firstName: string;
  lastName: string;
  previousNames?: string; // mapped from middleName
  dateOfBirth: string;
  gender: 'Male' | 'Female' | '';
  nationality?: string;
  /** NATIONAL_ID uses Zimbabwe ID format validation; PASSPORT only requires a value */
  identityDocumentType?: 'NATIONAL_ID' | 'PASSPORT';
  idNumber: string;
  religion?: string;
  disability?: string;
  addressDto: AddressDto;
  contactDetails: ContactDetails;
  qualificationDto: Qualification[];
  employmentHistoryDto: EmploymentHistory[];
  bankingInfoDto: BankingInfoDto[];
  nextOfKinListDto: NextOfKin[];
  loanOrDebtDto: LoanOrDebtDto[];
  hasCriminalRecords: boolean;
  criminalRecordDetails?: string;
  hasCivilJudgements: boolean;
  civilJudgementDetails?: string;
  maritalStatus: 'Married' | 'Single' | 'Divorced' | 'Widowed' | '';
  declarationAccepted: boolean;
  declarationSignature: string;
  declarationDate: string;
}

interface LoanApplicationPayload {
  businessInfoDto: BusinessInfoDto;
  loanInfo: LoanDetailsDto;
  personalInfo: PersonDto[];
  platformUsed: string;
  comment: string;
  userId?: string;
  username?: string;
  branchName?: string;
}

interface LoanApplicationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  initialData?: any;
  isUpdateMode?: boolean;
}

const LoanApplicationModal: React.FC<LoanApplicationModalProps> = ({ open, onClose, onSubmitSuccess, initialData, isUpdateMode = false }) => {
  const user = useDecodedToken();
  const steps = ['Business Info', 'Personal Info', 'Loan Details', 'Attachments', 'Review'];
  
  // Check if this is a "needs more information" status - only allow document upload
  // Use the EXACT same detection logic as canUpdateApplication in LoanApplications.tsx
  // Note: Backend returns loanStatus in LoanApplicationDto, list view has status
  const isNeedsMoreInfoMode = React.useMemo(() => {
    if (!isUpdateMode || !initialData) return false;
    // Backend returns loanStatus, but list view transforms it to status
    // Check both to handle both cases
    const status = (initialData.status || initialData.loanStatus || '').toLowerCase();
    const isNeedsMoreInfo = status === 'requesting for more info' || 
           status === 'requesting more info' || 
           status === 'additional info required' ||
           status === 'pending additional information' ||
           status === 'more_info_requested' ||
           status === 'more info requested';
    
    // Debug logging
    if (isUpdateMode && initialData) {
      console.log('[LOAN_APPLICATION_MODAL] Status check:', {
        status: initialData.status,
        loanStatus: initialData.loanStatus,
        normalizedStatus: status,
        isNeedsMoreInfoMode: isNeedsMoreInfo
      });
    }
    
    return isNeedsMoreInfo;
  }, [isUpdateMode, initialData]);
  
  // Start at step 0 by default, will be set to 3 in useEffect if needed
  const [activeStep, setActiveStep] = useState(0);
  const [currentField, setCurrentField] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [stepErrorFlags, setStepErrorFlags] = useState<boolean[]>([false, false, false, false, false]);
  const [stepErrorCounts, setStepErrorCounts] = useState<number[]>([0, 0, 0, 0, 0]);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [showDraftRestore, setShowDraftRestore] = useState(false);
  const [draftInfo, setDraftInfo] = useState<any>(null);
  // Silent auto-save; no user notifications

  // Session management
  const {
    showWarning,
    timeLeft,
    extendSession,
    saveAndLogout,
    forceLogout,
    startAutoSave,
    stopAutoSave
  } = useSessionManager({
    warningTime: 300, // 5 minute warning
    timeoutDuration: 7200, // 2 hours for loan application form
    autoSaveInterval: 30 // auto-save every 30 seconds
  });
  // SMEDCO branches
  const branches = [
    'Harare',
    'Bindura',
    'Mutare',
    'Gweru',
    'Masvingo',
    'Bulawayo',
    'Lupane'
  ];
  
  const createEmptyPerson = (): PersonDto => ({
    firstName: '', lastName: '', previousNames: '', identityDocumentType: 'NATIONAL_ID', idNumber: '', gender: '', maritalStatus: '', dateOfBirth: '',
    nationality: '', religion: '', disability: '',
    addressDto: { streetAddress: '', city: '', state: '', postalCode: '', country: '' },
    contactDetails: { phoneNumber: '', emailAddress: '', alternativePhone: '' },
    nextOfKinListDto: [], qualificationDto: [], employmentHistoryDto: [], loanOrDebtDto: [], bankingInfoDto: [],
    hasCriminalRecords: false, criminalRecordDetails: '', hasCivilJudgements: false, civilJudgementDetails: '',
    declarationAccepted: false, declarationSignature: '', declarationDate: ''
  });

  const defaultPayload = (): LoanApplicationPayload => ({
    businessInfoDto: {
      nameOfApplicant: '',
      nameOfBusiness: '',
      statusOfBusiness: '',
      formOfBusinessOwnership: '',
      tradeReferenceDto: [],
      bankingInfoDto: [],
      loanOrDebtDto: [],
      otherBusinesses: []
    },
    loanInfo: { loanAmount: undefined as number | undefined, loanPurpose: '', currency: '', loanProduct: '', tenure: '', branchName: '' },
    personalInfo: [createEmptyPerson()],
    platformUsed: 'website',
    comment: 'loan application',
    userId: user?.sub || '',
    username: user?.preferred_username || user?.email || '',
    branchName: '' // No default - user must select a branch
  });

  const [formData, setFormData] = useState<LoanApplicationPayload>(defaultPayload());
  
  // Product management state
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);


  // Reset to attachments step when opening in "needs more info" mode
  // In this mode, force step to 3 and prevent navigation
  useEffect(() => {
    if (open && isNeedsMoreInfoMode) {
      setActiveStep(3); // Force to attachments step only
      // Don't reset form data - keep it as is, just show attachments
    } else if (open && !isUpdateMode) {
      // Reset to step 0 for new applications
      setActiveStep(0);
    }
  }, [open, isNeedsMoreInfoMode, isUpdateMode]);
  
  // Prevent step navigation when in "needs more info" mode
  useEffect(() => {
    if (isNeedsMoreInfoMode && activeStep !== 3) {
      setActiveStep(3);
    }
  }, [isNeedsMoreInfoMode, activeStep]);
  
  // Populate form with initial data when in update mode
  useEffect(() => {
    // In "needs more info" mode, don't populate form - just show attachments
    if (isNeedsMoreInfoMode && open) {
      console.log('[LOAN_APPLICATION_MODAL] In needs more info mode - skipping form population');
      return;
    }
    
    if (isUpdateMode && initialData && open) {
      
      // Check if this is a loan application with detailed data or just basic info
      const hasDetailedData = initialData.businessInfo || initialData.loanDetails || initialData.personalDetails;
      
      if (hasDetailedData) {
        // Map the backend ClientLoan structure to the form structure
        const mappedData: LoanApplicationPayload = {
          businessInfoDto: {
            nameOfApplicant: initialData.businessInfo?.nameOfApplicant || '',
            nameOfBusiness: initialData.businessInfo?.nameOfBusiness || '',
            statusOfBusiness: initialData.businessInfo?.statusOfBusiness || '',
            formOfBusinessOwnership: initialData.businessInfo?.formOfBusinessOwnership || '',
            tradeReferenceDto: initialData.businessInfo?.tradeReferenceDto || [],
            bankingInfoDto: initialData.businessInfo?.bankingInfoDto || [],
            loanOrDebtDto: initialData.businessInfo?.loanOrDebtDto || [],
            otherBusinesses: initialData.businessInfo?.otherBusinesses || []
          },
          loanInfo: {
            loanAmount: initialData.loanDetails?.loanAmount,
            loanPurpose: initialData.loanDetails?.loanPurpose || '',
            currency: initialData.loanDetails?.currency || 'USD',
            loanProduct: initialData.loanDetails?.loanProduct || '',
            tenure: initialData.loanDetails?.tenure || '',
            branchName: initialData.branchName || 'Harare'
          },
          personalInfo: (initialData.personalDetails || [createEmptyPerson()]).map((person: any) => ({
            ...createEmptyPerson(),
            ...person,
            identityDocumentType: person?.identityDocumentType || 'NATIONAL_ID',
          })),
          platformUsed: initialData.platformUsed || 'website',
          comment: initialData.comment || 'loan application update',
          userId: user?.sub || '',
          username: user?.preferred_username || user?.email || '',
          branchName: initialData.branchName || 'Harare'
        };
        
        setFormData(mappedData);
      } else {
        // This is a basic loan application without detailed data - show a message

        // For now, just use default payload but show a message to the user
        setFormData(defaultPayload());
        setError('This loan application was created externally and does not have detailed information to edit. You can add additional information below.');
      }
    } else if (!isUpdateMode && open) {

      // Reset to default when creating new application
      setFormData(defaultPayload());
    }
  }, [isUpdateMode, initialData, open, user]);

  // Small helper to render clearer label-value pairs
  const Labeled: React.FC<{ label: string; value: any }> = ({ label, value }) => (
    <Typography variant="body2"><span style={{ fontWeight: 600 }}>{label}:</span> {value ?? '-'}</Typography>
  );

  // Helpers to preview files on Review step
  const openPreview = (file: File) => {
    try {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      // Let the browser manage revocation after navigation
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {}
  };

  const FileChip: React.FC<{ file: File }> = ({ file }) => (
    <Button
      variant="outlined"
      size="small"
      onClick={() => openPreview(file)}
      sx={{
        textTransform: 'none',
        px: 1.5,
        py: 0.25,
        borderRadius: 999,
        borderColor: 'grey.300'
      }}
    >
      {file.name || 'file'}
    </Button>
  );

  // Check for existing draft on component mount
  // Only load drafts for NEW applications, not for updates
  useEffect(() => {
    if (open && !isUpdateMode) {
      const checkForDraft = async () => {
        // Try to load from server first, fallback to local storage
        const draft = await formDraftService.loadDraftFromServer();
        if (draft && !draft.isComplete) {
          setDraftInfo(draft);
          setShowDraftRestore(true);
        }
      };
      checkForDraft();
    }
  }, [open, isUpdateMode]);

  // Auto-save functionality - debounced to prevent excessive calls
  // Only auto-save for NEW applications, not for updates
  useEffect(() => {
    if (open && !isUpdateMode) {
      // Start auto-save when form is open
      startAutoSave(formData, activeStep);
      
      return () => {
        stopAutoSave();
      };
    }
  }, [open, isUpdateMode, startAutoSave, stopAutoSave]);

  // Separate effect for auto-save with debouncing (silent)
  // Only auto-save for NEW applications, not for updates
  useEffect(() => {
    if (open && !isUpdateMode && formData && !success) {
      const timeoutId = setTimeout(() => {
        formDraftService.autoSave(formData, activeStep);
      }, 3000); // 3 second delay after last change

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [formData, activeStep, open, isUpdateMode, success]);

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      stopAutoSave();
    };
  }, [stopAutoSave]);

  // Load products from database when form opens
  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open]);

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      // Prefer live Skyfin products for loan applications
      let activeProducts: Product[] = [];
      try {
        activeProducts = await productApi.getActiveSkyfinProducts();
      } catch (skyfinErr) {
        console.warn('Skyfin products unavailable, falling back to local catalog:', skyfinErr);
      }

      if (!activeProducts || activeProducts.length === 0) {
        activeProducts = await productApi.getActiveProducts();
      }

      setProducts(activeProducts || []);
      
      if (formData.loanInfo.loanProduct) {
        const product = (activeProducts || []).find(p => p.productName === formData.loanInfo.loanProduct);
        if (product) {
          setSelectedProduct(product);
        }
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Update selected product when loanProduct changes and auto-fill currency/tenure from product
  useEffect(() => {
    if (formData.loanInfo.loanProduct && products.length > 0) {
      const product = products.find(p => p.productName === formData.loanInfo.loanProduct);
      setSelectedProduct(product || null);

      const nextCurrency = normalizeLoanCurrency(product?.currency);
      const tenureOptions = getProductTenureOptions(product);
      const patch: Partial<LoanDetailsDto> = {};
      if (nextCurrency && formData.loanInfo.currency !== nextCurrency) {
        patch.currency = nextCurrency;
      }
      if (formData.loanInfo.tenure && !tenureOptions.includes(String(formData.loanInfo.tenure))) {
        patch.tenure = tenureOptions[tenureOptions.length - 1] || '';
      }
      if (Object.keys(patch).length > 0) {
        setLoanInfo(patch);
      }
    } else if (!formData.loanInfo.loanProduct) {
      setSelectedProduct(null);
    }
  }, [formData.loanInfo.loanProduct, products]);

  // Calculate cost summary based on selected product and loan details.
  // interestRatePercentage is annual (% p.a.) from Skyfin/local products.
  const calculateCostSummary = () => {
    if (!selectedProduct || !formData.loanInfo.loanAmount || formData.loanInfo.loanAmount <= 0) {
      return null;
    }

    const loanAmount = formData.loanInfo.loanAmount;
    const tenureMonths = formData.loanInfo.tenure
      ? parseInt(formData.loanInfo.tenure, 10)
      : (selectedProduct.loanTenureMonths || 0);
    const annualRate = Number(selectedProduct.interestRatePercentage) || 0;
    const method = String(selectedProduct.interestMethod || '').toLowerCase();
    const amortization = String(selectedProduct.amortization || '').toLowerCase();
    const rateDescription = String(selectedProduct.interestRateDescription || '').toLowerCase();

    // Calculate Application Fee
    let applicationFee = 0;
    if (selectedProduct.applicationFeePercentage) {
      applicationFee = (loanAmount * selectedProduct.applicationFeePercentage) / 100;
      if (selectedProduct.applicationFeeMinimum && applicationFee < selectedProduct.applicationFeeMinimum) {
        applicationFee = selectedProduct.applicationFeeMinimum;
      }
      if (selectedProduct.applicationFeeMaximum && applicationFee > selectedProduct.applicationFeeMaximum) {
        applicationFee = selectedProduct.applicationFeeMaximum;
      }
    }

    // Calculate Administration Fees (one-time upfront)
    let administrationFees = 0;
    if (selectedProduct.administrationFeesPercentage) {
      administrationFees = (loanAmount * selectedProduct.administrationFeesPercentage) / 100;
    }

    // Service Fees
    const serviceFees = selectedProduct.serviceFeesAmount || 0;

    // --- Interest (aligned to product interest method) ---
    let totalInterest = 0;
    let monthlyRepayment = 0;
    let interestMethodLabel = selectedProduct.interestMethod || 'Declining Balance';

    if (annualRate > 0 && tenureMonths > 0) {
      const years = tenureMonths / 12;
      const isOnceOff =
        rateDescription.includes('once off') ||
        rateDescription.includes('once-off') ||
        rateDescription.includes('upfront');
      const isFlat =
        method.includes('flat') ||
        method.includes('simple') ||
        rateDescription.includes('flat') ||
        method.includes('rule of 78');
      const isMonthlyNominal = method.includes('monthly rate');
      const isDaily = method.includes('daily');
      const equalPrincipal =
        amortization.includes('equal principal') ||
        (amortization.includes('principal') && !amortization.includes('installment'));

      if (isOnceOff && (isFlat || !method)) {
        // Flat once-off: interest charged once on principal (not × tenure)
        totalInterest = (loanAmount * annualRate) / 100;
        monthlyRepayment = (loanAmount + totalInterest) / tenureMonths;
        interestMethodLabel = selectedProduct.interestMethod || 'Flat (once-off)';
      } else if (isFlat) {
        // Flat / Simple: Interest = Principal × annualRate × years
        totalInterest = loanAmount * (annualRate / 100) * years;
        monthlyRepayment = (loanAmount + totalInterest) / tenureMonths;
        interestMethodLabel = selectedProduct.interestMethod || 'Flat';
      } else if (isMonthlyNominal) {
        // Product rate is a monthly % applied each month on original principal
        totalInterest = loanAmount * (annualRate / 100) * tenureMonths;
        monthlyRepayment = (loanAmount + totalInterest) / tenureMonths;
        interestMethodLabel = selectedProduct.interestMethod || 'Monthly Rate';
      } else if (isDaily) {
        // Approximate daily accrual using annual rate over tenure days (~30.4167 days/month)
        const days = tenureMonths * (365 / 12);
        totalInterest = loanAmount * (annualRate / 100) * (days / 365);
        monthlyRepayment = (loanAmount + totalInterest) / tenureMonths;
        interestMethodLabel = selectedProduct.interestMethod || 'Daily Rate';
      } else if (equalPrincipal) {
        // Declining balance, equal principal portions
        const monthlyRate = annualRate / 1200;
        let remaining = loanAmount;
        const principalPerMonth = loanAmount / tenureMonths;
        for (let i = 0; i < tenureMonths; i++) {
          totalInterest += remaining * monthlyRate;
          remaining -= principalPerMonth;
        }
        monthlyRepayment = (loanAmount + totalInterest) / tenureMonths;
        interestMethodLabel = selectedProduct.interestMethod || 'Declining Balance (Equal Principal)';
      } else {
        // Declining balance / reducing / effective / compound — equal installments (EMI)
        // Matches LoanServiceImpl: monthlyRate = annual / 1200
        const monthlyRate = annualRate / 1200;
        if (monthlyRate === 0) {
          monthlyRepayment = loanAmount / tenureMonths;
          totalInterest = 0;
        } else {
          const factor = Math.pow(1 + monthlyRate, tenureMonths);
          monthlyRepayment = (loanAmount * monthlyRate * factor) / (factor - 1);
          totalInterest = monthlyRepayment * tenureMonths - loanAmount;
        }
        interestMethodLabel = selectedProduct.interestMethod || 'Declining Balance';
      }
    }

    const totalFees = applicationFee + administrationFees + serviceFees;
    const totalCost = totalFees + totalInterest;
    // Loan schedule repayment (principal + interest); fees shown separately as upfront costs
    const totalRepayment = loanAmount + totalInterest;
    const totalAmountPayable = totalRepayment + totalFees;

    return {
      loanAmount,
      tenureMonths,
      applicationFee,
      administrationFees,
      serviceFees,
      totalFees,
      totalInterest,
      totalCost,
      totalRepayment,
      totalAmountPayable,
      monthlyRepayment,
      annualRate,
      interestMethodLabel,
    };
  };

  const costSummary = calculateCostSummary();
  const [attachments, setAttachments] = useState<LoanAttachmentsState>(emptyLoanAttachments());

  const setBusiness = (patch: Partial<BusinessInfoDto>) => {
    const keyMap: Record<string, string> = {
      nameOfApplicant: 'b.nameOfApplicant',
      nameOfBusiness: 'b.nameOfBusiness',
      statusOfBusiness: 'b.statusOfBusiness',
      formOfBusinessOwnership: 'b.formOfBusinessOwnership',
    };
    Object.keys(patch).forEach((k) => {
      if (keyMap[k]) clearFieldErrors(keyMap[k]);
    });
    setFormData(prev => ({ ...prev, businessInfoDto: { ...prev.businessInfoDto, ...patch } }));
  };
  const setLoanInfo = (patch: Partial<LoanDetailsDto>) => {
    const keyMap: Record<string, string> = {
      loanAmount: 'l.loanAmount',
      currency: 'l.currency',
      loanProduct: 'l.loanProduct',
      tenure: 'l.tenure',
      branchName: 'l.branchName',
      loanPurpose: 'l.loanPurpose',
    };
    Object.keys(patch).forEach((k) => {
      if (keyMap[k]) clearFieldErrors(keyMap[k]);
    });
    setFormData(prev => ({ ...prev, loanInfo: { ...prev.loanInfo, ...patch } }));
  };
  const setPerson = (index: number, patch: Partial<PersonDto>) => {
    if (index === 0) {
      const keyMap: Record<string, string> = {
        firstName: 'p0.firstName',
        lastName: 'p0.lastName',
        idNumber: 'p0.idNumber',
        gender: 'p0.gender',
        maritalStatus: 'p0.maritalStatus',
        dateOfBirth: 'p0.dateOfBirth',
        declarationAccepted: 'p0.declarationAccepted',
        declarationSignature: 'p0.declarationSignature',
        declarationDate: 'p0.declarationDate',
      };
      Object.keys(patch).forEach((k) => {
        if (keyMap[k]) clearFieldErrors(keyMap[k]);
      });
      if ((patch as any).contactDetails) {
        clearFieldErrors('p0.phoneNumber', 'p0.emailAddress');
      }
      if ((patch as any).addressDto) {
        clearFieldErrors('p0.address.streetAddress', 'p0.address.city', 'p0.address.country', 'p0.address.state');
      }
    }
    setFormData(prev => {
      const arr = [...prev.personalInfo];
      while (arr.length <= index) arr.push(createEmptyPerson());
      arr[index] = { ...arr[index], ...patch } as PersonDto;
      return { ...prev, personalInfo: arr };
    });
  };

  const enforceOwnershipMinMax = () => {
    const type = formData.businessInfoDto.formOfBusinessOwnership;
    let min = 1, max = 1; // Sole Proprietorship default
    if (type === 'Company') { min = 1; max = 10; }
    else if (type === 'Co-operative') { min = 3; max = 20; }
    else if (type === 'Partnership') { min = 2; max = 5; }
    else if (type === 'Other') { min = 1; max = 10; }
    setFormData(prev => {
      let people = [...prev.personalInfo];
      if (people.length < min) {
        while (people.length < min) people.push(createEmptyPerson());
      } else if (people.length > max) {
        people = people.slice(0, max);
      }
      return { ...prev, personalInfo: people };
    });
  };

  const clearFieldErrors = (...keys: string[]) => {
    setValidationErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      keys.forEach((k) => {
        if (next[k]) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const fieldErrorProps = (key: string) => ({
    error: Boolean(validationErrors[key]),
    helperText: validationErrors[key] || undefined,
    inputProps: { 'data-field-key': key },
  });

  const collectStepErrors = (step: number): Record<string, string> => {
    const errs: Record<string, string> = {};
    const b = formData.businessInfoDto;
    const l = formData.loanInfo;
    const p0 = formData.personalInfo[0];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^(\+?263|0)?[0-9]{9,12}$/;

    if (step === 0) {
      if (!b.nameOfApplicant?.trim()) errs['b.nameOfApplicant'] = 'Applicant name is required';
      if (!b.nameOfBusiness?.trim()) errs['b.nameOfBusiness'] = 'Business name is required';
      if (!b.statusOfBusiness) errs['b.statusOfBusiness'] = 'Business status is required';
      if (!b.formOfBusinessOwnership) errs['b.formOfBusinessOwnership'] = 'Ownership type is required';
    }

    if (step === 1) {
      if (!p0.firstName?.trim()) errs['p0.firstName'] = 'First name is required';
      if (!p0.lastName?.trim()) errs['p0.lastName'] = 'Last name is required';
      if (!p0.identityDocumentType) errs['p0.identityDocumentType'] = 'Select ID or Passport';
      if (!p0.idNumber?.trim()) {
        errs['p0.idNumber'] = p0.identityDocumentType === 'PASSPORT'
          ? 'Passport number is required'
          : 'National ID is required';
      } else if ((p0.identityDocumentType || 'NATIONAL_ID') === 'NATIONAL_ID' && !isValidZimbabweNationalId(p0.idNumber)) {
        errs['p0.idNumber'] = 'Enter a valid Zimbabwe National ID (e.g. 12-345678Z12)';
      } else if (p0.identityDocumentType === 'PASSPORT' && p0.idNumber.trim().length < 5) {
        errs['p0.idNumber'] = 'Enter a valid passport number';
      }
      if (!p0.gender) errs['p0.gender'] = 'Gender is required';
      if (!p0.maritalStatus) errs['p0.maritalStatus'] = 'Marital status is required';
      if (!p0.dateOfBirth) {
        errs['p0.dateOfBirth'] = 'Date of birth is required';
      } else {
        const birthDate = new Date(p0.dateOfBirth);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
        if (actualAge < 18) errs['p0.dateOfBirth'] = 'You must be at least 18 years old';
      }
      if (!p0.contactDetails.phoneNumber || !phoneRegex.test(p0.contactDetails.phoneNumber)) {
        errs['p0.phoneNumber'] = 'Enter a valid phone number';
      }
      if (!p0.contactDetails.emailAddress || !emailRegex.test(p0.contactDetails.emailAddress)) {
        errs['p0.emailAddress'] = 'Enter a valid email address';
      }
      if (!p0.addressDto.streetAddress?.trim()) errs['p0.address.streetAddress'] = 'Street address is required';
      if (!p0.addressDto.city?.trim()) errs['p0.address.city'] = 'City is required';
      if (!p0.addressDto.country?.trim()) errs['p0.address.country'] = 'Country is required';
      if (!p0.addressDto.state?.trim()) errs['p0.address.state'] = 'Province is required';
      if (!p0.declarationAccepted) errs['p0.declarationAccepted'] = 'You must accept the terms to proceed';
      if (!p0.declarationSignature?.trim()) errs['p0.declarationSignature'] = 'Provide your full name as signature';
      if (!p0.declarationDate) errs['p0.declarationDate'] = 'Provide declaration date';
    }

    if (step === 2) {
      if (!l.loanProduct?.trim()) {
        errs['l.loanProduct'] = 'Loan product is required';
      } else {
        const product =
          selectedProduct?.productName === l.loanProduct
            ? selectedProduct
            : products.find((p) => p.productName === l.loanProduct);
        const applicantGender = formData.personalInfo[0]?.gender;
        if (product && !isApplicantEligibleForProduct(applicantGender, product)) {
          errs['l.loanProduct'] =
            productGenderRestrictionMessage(product) ||
            'You are not eligible for this product based on gender';
        }
      }
      if (!l.currency) errs['l.currency'] = 'Currency is required';
      if (!l.loanAmount || Number(l.loanAmount) <= 0) {
        errs['l.loanAmount'] = 'Enter a valid amount';
      } else if (selectedProduct) {
        if (selectedProduct.minimumPrincipal && l.loanAmount < selectedProduct.minimumPrincipal) {
          errs['l.loanAmount'] = `Minimum amount is ${selectedProduct.minimumPrincipal.toLocaleString()}`;
        } else if (selectedProduct.maximumPrincipal && l.loanAmount > selectedProduct.maximumPrincipal) {
          errs['l.loanAmount'] = `Maximum amount is ${selectedProduct.maximumPrincipal.toLocaleString()}`;
        }
      }
      if (!l.tenure || String(l.tenure).trim() === '') {
        errs['l.tenure'] = 'Tenure is required';
      } else {
        const allowed = getProductTenureOptions(selectedProduct);
        if (!allowed.includes(String(l.tenure))) {
          errs['l.tenure'] = `Tenure must be one of: ${allowed.join(', ')} months for this product`;
        }
      }
      if (!l.branchName || String(l.branchName).trim() === '') errs['l.branchName'] = 'Branch selection is required';
      if (!l.loanPurpose?.trim()) errs['l.loanPurpose'] = 'Loan purpose is required';
    }

    if (step === 3) {
      const hasAnyAttachment = Object.values(attachments).some((v) => {
        if (!v) return false;
        if (Array.isArray(v)) return v.length > 0;
        return true;
      });
      if (!hasAnyAttachment) {
        errs['a.attachments'] = 'Upload at least one supporting document before continuing';
      }
    }

    return errs;
  };

  const applyStepErrorState = (errs: Record<string, string>, step: number) => {
    const prefixes = [
      ['b.'],
      ['p0.'],
      ['l.'],
      ['a.'],
      [],
    ][step] || [];

    setValidationErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (prefixes.some((p) => k.startsWith(p))) delete next[k];
      });
      Object.assign(next, errs);
      return next;
    });

    const flags = [...stepErrorFlags];
    const counts = [...stepErrorCounts];
    flags[step] = Object.keys(errs).length > 0;
    counts[step] = Object.keys(errs).length;
    setStepErrorFlags(flags);
    setStepErrorCounts(counts);
    setShowValidationSummary(Object.keys(errs).length > 0);
  };

  const validateStep = (step: number): boolean => {
    const errs = collectStepErrors(step);
    applyStepErrorState(errs, step);
    return Object.keys(errs).length === 0;
  };

  const scrollToFirstStepError = (errs: Record<string, string>) => {
    try {
      const content = document.querySelector('.MuiDialogContent-root');
      content?.scrollTo({ top: 0, behavior: 'smooth' });
      const firstKey = Object.keys(errs)[0];
      if (!firstKey) return;
      // Prefer the field marked with data-field-key, else any MUI error state
      window.setTimeout(() => {
        const byKey = document.querySelector(`[data-field-key="${firstKey}"]`) as HTMLElement | null;
        const fallback = document.querySelector('.Mui-error, [aria-invalid="true"]') as HTMLElement | null;
        (byKey || fallback)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        byKey?.focus?.();
      }, 80);
    } catch {
      // ignore
    }
  };

  const handleNext = () => {
    // Gate every section before Review; do not allow skipping incomplete steps
    if (activeStep <= 3) {
      const errs = collectStepErrors(activeStep);
      applyStepErrorState(errs, activeStep);
      if (Object.keys(errs).length > 0) {
        const count = Object.keys(errs).length;
        setError(
          `Please fix ${count} issue${count === 1 ? '' : 's'} in ${steps[activeStep]} before continuing.`
        );
        scrollToFirstStepError(errs);
        return;
      }
    }
    setError(null);
    setShowValidationSummary(false);
    setActiveStep((s) => Math.min(steps.length - 1, s + 1));
  };

  const isFormValid = () => {
    return [0, 1, 2, 3].every((step) => Object.keys(collectStepErrors(step)).length === 0);
  };

  const validateFormAndSetErrors = (): boolean => {
    const allErrs: Record<string, string> = {
      ...collectStepErrors(0),
      ...collectStepErrors(1),
      ...collectStepErrors(2),
      ...collectStepErrors(3),
    };
    setValidationErrors(allErrs);

    const flags = [false, false, false, false, false];
    const counts = [0, 0, 0, 0, 0];
    Object.keys(allErrs).forEach((k) => {
      if (k.startsWith('b.')) { flags[0] = true; counts[0]++; }
      else if (k.startsWith('p0.')) { flags[1] = true; counts[1]++; }
      else if (k.startsWith('l.')) { flags[2] = true; counts[2]++; }
      else if (k.startsWith('a.')) { flags[3] = true; counts[3]++; }
    });
    setStepErrorFlags(flags);
    setStepErrorCounts(counts);
    if (flags.includes(true)) {
      const firstIdx = flags.findIndex((f) => f);
      setActiveStep(firstIdx);
      setShowValidationSummary(true);
      scrollToFirstStepError(allErrs);
    }
    return Object.keys(allErrs).length === 0;
  };

  // Draft restoration functions
  const handleRestoreDraft = () => {
    if (draftInfo) {
      setFormData(draftInfo.formData);
      setActiveStep(draftInfo.step);
      setShowDraftRestore(false);
      setDraftInfo(null);
      // Silent draft restore
    }
  };

  const handleDiscardDraft = () => {
    formDraftService.clearDraftLocally();
    setShowDraftRestore(false);
    setDraftInfo(null);
  };

  // Manual save removed; drafts save automatically

  // Session extended notification removed

  // Handle form field interactions without interfering with typing
  const handleFormInteraction = (e: React.SyntheticEvent) => {
    // This will be called on form field changes but won't interfere with typing

  };

  // Helper functions for file management
  const removeSingleFile = (key: string) => {
    setAttachments(prev => ({ ...prev, [key]: null }));
  };

  const removeMultiFile = (key: string, index: number) => {
    setAttachments(prev => {
      const currentFiles = (prev[key] as File[]) || [];
      const newFiles = currentFiles.filter((_, i) => i !== index);
      return { ...prev, [key]: newFiles };
    });
  };

  const clearAllFiles = (key: string) => {
    setAttachments(prev => ({ ...prev, [key]: [] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check authentication before allowing submission
    if (!isAuthenticated()) {

      setError('Authentication required. Please log in again.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      return;
    }
    
    // In "needs more info" mode, skip form validation - only check for files
    const documentUploads = collectDocumentUploadItems(attachments);
    if (isNeedsMoreInfoMode) {
      if (documentUploads.length === 0) {
        setError('Please upload at least one document.');
        setLoading(false);
        return;
      }
    } else {
      // Always run validation to show users where to fix
      const ok = validateFormAndSetErrors();
      if (!ok) return;
    }
    setLoading(true);
    setError(null);
    try {
      // Collect labeled files first (needed for both modes)
      const files = documentUploads;
      
      // Use update or create based on mode
      let loanId: string;
      if (isUpdateMode && initialData?.id) {
        if (isNeedsMoreInfoMode) {
          // In "needs more info" mode, ONLY upload documents - skip ALL validation and branch checks
          // In "needs more info" mode, only upload documents, don't update the application
          loanId = initialData.id;
          if (files.length > 0) {
            await uploadLoanAttachmentsBatched(loanId, files, 3);
          } else {
            setError('Please upload at least one document.');
            setLoading(false);
            return;
          }
        } else {
          // Normal update mode - validate form and check branch
          if (!validateFormAndSetErrors()) {
            setError('Please correct the highlighted errors.');
            setLoading(false);
            return;
          }
          
          // Ensure branch from loanInfo is also set at top level for backend
          const selectedBranch = formData.loanInfo.branchName || formData.branchName;
          if (!selectedBranch || selectedBranch.trim() === '') {
            setError('Please select a branch before submitting the application.');
            setLoading(false);
            return;
          }
          
          const payloadToSubmit = {
            ...formData,
            branchName: selectedBranch,
            loanInfo: {
              ...formData.loanInfo,
              branchName: selectedBranch
            }
          };
          
          // Normal update mode - update application and upload attachments
          ({ loanId } = await updateLoanApplication(initialData.id, payloadToSubmit));
          
          // Upload new attachments if any
          if (files.length > 0) {
            await uploadLoanAttachmentsBatched(loanId, files, 3);
          }
        }
      } else {
        // Create new loan application - validate form and check branch
        if (!validateFormAndSetErrors()) {
          setError('Please correct the highlighted errors.');
          setLoading(false);
          return;
        }
        
        // Ensure branch from loanInfo is also set at top level for backend
        const selectedBranch = formData.loanInfo.branchName || formData.branchName;
        if (!selectedBranch || selectedBranch.trim() === '') {
          setError('Please select a branch before submitting the application.');
          setLoading(false);
          return;
        }
        
        const payloadToSubmit = {
          ...formData,
          branchName: selectedBranch,
          loanInfo: {
            ...formData.loanInfo,
            branchName: selectedBranch
          }
        };
        
        // Create new loan application
        if (files.length > 0) {
          ({ loanId } = await createLoanApplicationWithAttachments(payloadToSubmit, files));
        } else {
          ({ loanId } = await createLoanApplicationJson(payloadToSubmit));
        }
      }
      
      // Only mark as complete and clear draft after successful submission
      // Only for NEW applications, not updates
      if (!isUpdateMode) {
        formDraftService.markDraftComplete();
        formDraftService.clearDraftLocally();
      }
      setSuccess(true);
      stopAutoSave();
      setTimeout(() => {
        onSubmitSuccess();
        onClose();
        if (!isUpdateMode) {
          setFormData(defaultPayload());
        setAttachments(emptyLoanAttachments());
        setActiveStep(0);
        setSuccess(false);
        }
      }, 5000);
    } catch (err) {
      // Persist a draft if submission fails (extra safety)
      // Only for NEW applications, not updates
      if (!isUpdateMode) {
        try {
          formDraftService.saveDraftLocally(formData, activeStep);
          await formDraftService.saveDraftToServer(formData, activeStep);
        } catch {}
      }
      // Keep the current form values and attachments; show error
      setError('Failed to submit loan application. Your progress was saved as a draft. Please fix the highlighted issues and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    // For update mode, close directly without asking about drafts
    if (isUpdateMode) {
      onClose();
      return;
    }
    // For new applications, ask about saving draft
    setConfirmCancelOpen(true);
  };

  const p0 = formData.personalInfo[0];

  return (
    <Dialog 
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      disableEscapeKeyDown
      maxWidth="md" 
      fullWidth 
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isUpdateMode ? 'Update Loan Application' : 'Loan Application'}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={handleClose} disabled={loading}><CloseIcon /></IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {isUpdateMode ? 'Loan application updated successfully!' : 'Loan application submitted successfully!'}
            </Alert>
            <Typography>Redirecting back to loan applications...</Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {!isNeedsMoreInfoMode && (
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((s, idx) => (
                  <Step key={s} completed={idx < activeStep && !stepErrorFlags[idx]}>
                    <StepLabel
                      error={stepErrorFlags[idx]}
                      optional={stepErrorFlags[idx] ? (
                        <Typography variant="caption" color="error">{stepErrorCounts[idx]} error{stepErrorCounts[idx] === 1 ? '' : 's'}</Typography>
                      ) : undefined}
                      onClick={() => {
                        // Allow going back to earlier steps; re-validate current first if moving forward
                        if (idx < activeStep) {
                          setActiveStep(idx);
                          setError(null);
                        } else if (idx > activeStep) {
                          // Must pass current section before jumping ahead
                          const errs = collectStepErrors(activeStep);
                          applyStepErrorState(errs, activeStep);
                          if (Object.keys(errs).length > 0) {
                            setError(`Complete ${steps[activeStep]} before moving to ${s}.`);
                            scrollToFirstStepError(errs);
                            return;
                          }
                          // Walk forward step-by-step so each section is validated
                          let step = activeStep;
                          while (step < idx) {
                            const stepErrs = collectStepErrors(step);
                            if (Object.keys(stepErrs).length > 0) {
                              applyStepErrorState(stepErrs, step);
                              setActiveStep(step);
                              setError(`Complete ${steps[step]} before moving to ${s}.`);
                              scrollToFirstStepError(stepErrs);
                              return;
                            }
                            step += 1;
                          }
                          setError(null);
                          setShowValidationSummary(false);
                          setActiveStep(idx);
                        }
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      {s}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
            {isNeedsMoreInfoMode && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'info.dark', fontWeight: 600 }}>
                  Additional Documents Required
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your loan application requires additional documents. Please upload the requested documents below. 
                  You cannot modify other application details at this stage.
                </Typography>
              </Box>
            )}
            <form onSubmit={handleSubmit}>
              {showValidationSummary && Object.keys(validationErrors).length > 0 && (
                <Grid item xs={12} sx={{ width: '100%', mb: 1 }}>
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowValidationSummary(false)}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Fix these issues in {steps[activeStep]}:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {Object.entries(validationErrors)
                        .filter(([key]) => {
                          if (activeStep === 0) return key.startsWith('b.');
                          if (activeStep === 1) return key.startsWith('p0.');
                          if (activeStep === 2) return key.startsWith('l.');
                          if (activeStep === 3) return key.startsWith('a.');
                          return true;
                        })
                        .map(([key, msg]) => (
                          <li key={key}>
                            <Typography variant="body2">{msg}</Typography>
                          </li>
                        ))}
                    </Box>
                  </Alert>
                </Grid>
              )}
              <Grid container spacing={3}>
                {!isNeedsMoreInfoMode && activeStep === 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Business Information</Typography>
                    </Grid>
                    
                    {/* Basic Business Information */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Basic Business Information</Typography></Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={<LabelWithHelp text="Name of Applicant" helpKey="nameOfApplicant" />}
                        value={formData.businessInfoDto.nameOfApplicant}
                        onChange={e => setBusiness({ nameOfApplicant: e.target.value })}
                        onFocus={() => setCurrentField('nameOfApplicant')}
                        placeholder="e.g., John Muzenda"
                        required
                        {...fieldErrorProps('b.nameOfApplicant')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={<LabelWithHelp text="Business Name" helpKey="nameOfBusiness" />}
                        value={formData.businessInfoDto.nameOfBusiness}
                        onChange={e => setBusiness({ nameOfBusiness: e.target.value })}
                        onFocus={() => setCurrentField('nameOfBusiness')}
                        placeholder="e.g., ABC Trading Pvt Ltd"
                        required
                        {...fieldErrorProps('b.nameOfBusiness')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required error={Boolean(validationErrors['b.statusOfBusiness'])} data-field-key="b.statusOfBusiness">
                        <InputLabel>
                          <LabelWithHelp text="Business Status *" helpKey="statusOfBusiness" />
                        </InputLabel>
                        <Select
                          value={formData.businessInfoDto.statusOfBusiness}
                          label="Business Status *"
                          onFocus={() => setCurrentField('statusOfBusiness')}
                          onChange={(e: SelectChangeEvent<string>) => setBusiness({ statusOfBusiness: e.target.value as BusinessInfoDto['statusOfBusiness'] })}
                        >
                          <MenuItem value="New">New (Just Starting)</MenuItem>
                          <MenuItem value="Operating">Operating (Already Running)</MenuItem>
                        </Select>
                        {validationErrors['b.statusOfBusiness'] && (
                          <FormHelperText>{validationErrors['b.statusOfBusiness']}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={Boolean(validationErrors['b.formOfBusinessOwnership'])} data-field-key="b.formOfBusinessOwnership">
                        <InputLabel>
                          <LabelWithHelp text="Form of Business Ownership *" helpKey="formOfBusinessOwnership" />
                        </InputLabel>
                        <Select
                          value={formData.businessInfoDto.formOfBusinessOwnership}
                          label="Form of Business Ownership *"
                          onFocus={() => setCurrentField('formOfBusinessOwnership')}
                          onChange={(e: SelectChangeEvent<string>) => { setBusiness({ formOfBusinessOwnership: e.target.value as BusinessInfoDto['formOfBusinessOwnership'] }); enforceOwnershipMinMax(); }}>
                          <MenuItem value="Company">Company</MenuItem>
                          <MenuItem value="Co-operative">Co-operative</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                          <MenuItem value="Partnership">Partnership</MenuItem>
                          <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
                    </Select>
                        {validationErrors['b.formOfBusinessOwnership'] && (
                          <FormHelperText>{validationErrors['b.formOfBusinessOwnership']}</FormHelperText>
                        )}
                  </FormControl>
                </Grid>
                
                    {/* Trade References */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Trade References</Typography>
                        {FIELD_HELP.tradeReferenceDto && (
                          <FieldHelpIcon
                            title={FIELD_HELP.tradeReferenceDto.description}
                            tip={FIELD_HELP.tradeReferenceDto.tip}
                          />
                        )}
                      </Box>
                    </Grid>
                    {formData.businessInfoDto.tradeReferenceDto.map((ref, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Trade Reference {index + 1}</Typography>
                            <Tooltip title="Remove Trade Reference">
                              <IconButton color="error" onClick={() => {
                                const newRefs = [...formData.businessInfoDto.tradeReferenceDto];
                                newRefs.splice(index, 1);
                                setBusiness({ tradeReferenceDto: newRefs });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Trade Name" value={ref.tradeName} onChange={e => {
                          const newRefs = [...formData.businessInfoDto.tradeReferenceDto];
                          newRefs[index] = { ...newRefs[index], tradeName: e.target.value };
                          setBusiness({ tradeReferenceDto: newRefs });
                        }} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Account Number" value={ref.accountNumber} onChange={e => {
                          const newRefs = [...formData.businessInfoDto.tradeReferenceDto];
                          newRefs[index] = { ...newRefs[index], accountNumber: e.target.value };
                          setBusiness({ tradeReferenceDto: newRefs });
                        }} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Phone Number" value={ref.phoneNumber} onChange={e => {
                          const newRefs = [...formData.businessInfoDto.tradeReferenceDto];
                          newRefs[index] = { ...newRefs[index], phoneNumber: e.target.value };
                          setBusiness({ tradeReferenceDto: newRefs });
                        }} /></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setBusiness({ tradeReferenceDto: [...formData.businessInfoDto.tradeReferenceDto, { tradeName: '', accountNumber: '', phoneNumber: '' }] })}>
                        Add Trade Reference
                      </Button>
                </Grid>
                
                    {/* Banking Information */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Banking Information</Typography></Grid>
                    {formData.businessInfoDto.bankingInfoDto.map((bank, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Bank Account {index + 1}</Typography>
                            <Tooltip title="Remove Bank Account">
                              <IconButton color="error" onClick={() => {
                                const newBanks = [...formData.businessInfoDto.bankingInfoDto];
                                newBanks.splice(index, 1);
                                setBusiness({ bankingInfoDto: newBanks });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Bank Name" value={bank.bankName} onChange={e => {
                          const newBanks = [...formData.businessInfoDto.bankingInfoDto];
                          newBanks[index] = { ...newBanks[index], bankName: e.target.value };
                          setBusiness({ bankingInfoDto: newBanks });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Branch" value={bank.branch} onChange={e => {
                          const newBanks = [...formData.businessInfoDto.bankingInfoDto];
                          newBanks[index] = { ...newBanks[index], branch: e.target.value };
                          setBusiness({ bankingInfoDto: newBanks });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Account Type" value={bank.accountType} onChange={e => {
                          const newBanks = [...formData.businessInfoDto.bankingInfoDto];
                          newBanks[index] = { ...newBanks[index], accountType: e.target.value };
                          setBusiness({ bankingInfoDto: newBanks });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Account Number" value={bank.accountNumber} onChange={e => {
                          const newBanks = [...formData.businessInfoDto.bankingInfoDto];
                          newBanks[index] = { ...newBanks[index], accountNumber: e.target.value };
                          setBusiness({ bankingInfoDto: newBanks });
                        }} /></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setBusiness({ bankingInfoDto: [...formData.businessInfoDto.bankingInfoDto, { bankName: '', branch: '', accountType: '', accountNumber: '' }] })}>
                        Add Bank Account
                      </Button>
                </Grid>
                
                    {/* Loans and Debts */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Loans and Debts</Typography></Grid>
                    {formData.businessInfoDto.loanOrDebtDto.map((debt, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Loan/Debt {index + 1}</Typography>
                            <Tooltip title="Remove Loan/Debt">
                              <IconButton color="error" onClick={() => {
                                const newDebts = [...formData.businessInfoDto.loanOrDebtDto];
                                newDebts.splice(index, 1);
                                setBusiness({ loanOrDebtDto: newDebts });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="To Whom" value={debt.toWhom} onChange={e => {
                          const newDebts = [...formData.businessInfoDto.loanOrDebtDto];
                          newDebts[index] = { ...newDebts[index], toWhom: e.target.value };
                          setBusiness({ loanOrDebtDto: newDebts });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Amount Owed" type="number" value={debt.amountOwed} onChange={e => {
                          const newDebts = [...formData.businessInfoDto.loanOrDebtDto];
                          newDebts[index] = { ...newDebts[index], amountOwed: parseFloat(e.target.value) || 0 };
                          setBusiness({ loanOrDebtDto: newDebts });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="purpose" value={debt.purpose} onChange={e => {
                          const newDebts = [...formData.businessInfoDto.loanOrDebtDto];
                          newDebts[index] = { ...newDebts[index], purpose: e.target.value };
                          setBusiness({ loanOrDebtDto: newDebts });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Monthly Repayment" type="number" value={debt.monthlyRepayment} onChange={e => {
                          const newDebts = [...formData.businessInfoDto.loanOrDebtDto];
                          newDebts[index] = { ...newDebts[index], monthlyRepayment: parseFloat(e.target.value) || 0 };
                          setBusiness({ loanOrDebtDto: newDebts });
                        }} /></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setBusiness({ loanOrDebtDto: [...formData.businessInfoDto.loanOrDebtDto, { toWhom: '', amountOwed: 0, purpose: '', monthlyRepayment: 0 }] })}>
                        Add Loan/Debt
                      </Button>
                </Grid>
                
                    {/* Other Businesses */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Other Businesses</Typography></Grid>
                    {formData.businessInfoDto.otherBusinesses.map((business, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Other Business {index + 1}</Typography>
                            <Tooltip title="Remove Other Business">
                              <IconButton color="error" onClick={() => {
                                const newBusinesses = [...formData.businessInfoDto.otherBusinesses];
                                newBusinesses.splice(index, 1);
                                setBusiness({ otherBusinesses: newBusinesses });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Name of Business" value={business.nameOfBusiness} onChange={e => {
                          const newBusinesses = [...formData.businessInfoDto.otherBusinesses];
                          newBusinesses[index] = { ...newBusinesses[index], nameOfBusiness: e.target.value };
                          setBusiness({ otherBusinesses: newBusinesses });
                        }} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Nature of Operation" value={business.natureOfOperation} onChange={e => {
                          const newBusinesses = [...formData.businessInfoDto.otherBusinesses];
                          newBusinesses[index] = { ...newBusinesses[index], natureOfOperation: e.target.value };
                          setBusiness({ otherBusinesses: newBusinesses });
                        }} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Location" value={business.location} onChange={e => {
                          const newBusinesses = [...formData.businessInfoDto.otherBusinesses];
                          newBusinesses[index] = { ...newBusinesses[index], location: e.target.value };
                          setBusiness({ otherBusinesses: newBusinesses });
                        }} /></Grid>
                      </React.Fragment>
                    ))}
                <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setBusiness({ otherBusinesses: [...formData.businessInfoDto.otherBusinesses, { nameOfBusiness: '', natureOfOperation: '', location: '' }] })}>
                        Add Other Business
                      </Button>
                </Grid>
                  </>
                )}

                {!isNeedsMoreInfoMode && activeStep === 1 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Personal Information</Typography>
                    </Grid>
                    
                    {/* Basic Personal Details */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Basic Personal Details</Typography></Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={<LabelWithHelp text="First Name" helpKey="firstName" />}
                        value={p0.firstName}
                        onChange={e => setPerson(0, { firstName: e.target.value })}
                        onFocus={() => setCurrentField('firstName')}
                        placeholder="e.g., John"
                        required
                        {...fieldErrorProps('p0.firstName')}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={<LabelWithHelp text="Last Name" helpKey="lastName" />}
                        value={p0.lastName}
                        onChange={e => setPerson(0, { lastName: e.target.value })}
                        onFocus={() => setCurrentField('lastName')}
                        placeholder="e.g., Muzenda"
                        required
                        {...fieldErrorProps('p0.lastName')}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={<LabelWithHelp text="Previous Names (Optional)" helpKey="otherNames" />}
                        value={p0.previousNames}
                        onChange={e => setPerson(0, { previousNames: e.target.value })}
                        onFocus={() => setCurrentField('previousNames')}
                        placeholder="e.g., Middle name"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth required error={Boolean(validationErrors['p0.identityDocumentType'])}>
                        <InputLabel id="identity-document-type-label">ID Type</InputLabel>
                        <Select
                          labelId="identity-document-type-label"
                          label="ID Type"
                          value={p0.identityDocumentType || 'NATIONAL_ID'}
                          onChange={(e: SelectChangeEvent) => {
                            const nextType = e.target.value as 'NATIONAL_ID' | 'PASSPORT';
                            setPerson(0, { identityDocumentType: nextType });
                            clearFieldErrors('p0.identityDocumentType', 'p0.idNumber');
                          }}
                          inputProps={{ 'data-field-key': 'p0.identityDocumentType' }}
                        >
                          <MenuItem value="NATIONAL_ID">National ID</MenuItem>
                          <MenuItem value="PASSPORT">Passport</MenuItem>
                        </Select>
                        {validationErrors['p0.identityDocumentType'] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {validationErrors['p0.identityDocumentType']}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={
                          <LabelWithHelp
                            text={(p0.identityDocumentType || 'NATIONAL_ID') === 'PASSPORT' ? 'Passport Number' : 'National ID Number'}
                            helpKey="idNumber"
                          />
                        }
                        value={p0.idNumber}
                        onChange={e => {
                          setPerson(0, { idNumber: e.target.value });
                          clearFieldErrors('p0.idNumber');
                        }}
                        onBlur={() => {
                          const value = p0.idNumber?.trim();
                          if (!value) return;
                          if ((p0.identityDocumentType || 'NATIONAL_ID') === 'NATIONAL_ID' && !isValidZimbabweNationalId(value)) {
                            setValidationErrors(prev => ({
                              ...prev,
                              'p0.idNumber': 'Enter a valid Zimbabwe National ID (e.g. 12-345678Z12)',
                            }));
                          }
                        }}
                        onFocus={() => setCurrentField('idNumber')}
                        placeholder={(p0.identityDocumentType || 'NATIONAL_ID') === 'PASSPORT' ? 'e.g., FN123456' : 'e.g., 12-345678Z12'}
                        required
                        {...fieldErrorProps('p0.idNumber')}
                        helperText={
                          validationErrors['p0.idNumber'] ||
                          ((p0.identityDocumentType || 'NATIONAL_ID') === 'NATIONAL_ID'
                            ? 'Format: 12-345678Z12'
                            : undefined)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField 
                        type="date" 
                        fullWidth 
                        label={<LabelWithHelp text="Date of Birth" helpKey="dateOfBirth" />}
                        InputLabelProps={{ shrink: true }} 
                        value={p0.dateOfBirth} 
                        onChange={e => {
                          const selectedDate = e.target.value;
                          setPerson(0, { dateOfBirth: selectedDate });
                          
                          // Real-time age validation
                          if (selectedDate) {
                            const birthDate = new Date(selectedDate);
                            const today = new Date();
                            const age = today.getFullYear() - birthDate.getFullYear();
                            const monthDiff = today.getMonth() - birthDate.getMonth();
                            const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
                            
                            if (actualAge < 18) {
                              setError('You must be at least 18 years old to apply for a loan.');
                            } else {
                              setError(null);
                            }
                          }
                        }} 
                        required 
                        inputProps={{
                          max: new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0],
                          'data-field-key': 'p0.dateOfBirth',
                        }}
                        error={Boolean(validationErrors['p0.dateOfBirth']) || (p0.dateOfBirth ? (() => {
                          const birthDate = new Date(p0.dateOfBirth);
                          const today = new Date();
                          const age = today.getFullYear() - birthDate.getFullYear();
                          const monthDiff = today.getMonth() - birthDate.getMonth();
                          const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
                          return actualAge < 18;
                        })() : false)}
                        helperText={
                          validationErrors['p0.dateOfBirth'] ||
                          (p0.dateOfBirth && (() => {
                            const birthDate = new Date(p0.dateOfBirth);
                            const today = new Date();
                            const age = today.getFullYear() - birthDate.getFullYear();
                            const monthDiff = today.getMonth() - birthDate.getMonth();
                            const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
                            return actualAge < 18 ? 'You must be at least 18 years old to apply' : undefined;
                          })())
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth required error={Boolean(validationErrors['p0.gender'])} data-field-key="p0.gender">
                        <InputLabel>Gender *</InputLabel>
                        <Select
                          value={p0.gender}
                          label="Gender *"
                          onChange={(e: SelectChangeEvent<string>) => {
                            const nextGender = e.target.value as PersonDto['gender'];
                            setPerson(0, { gender: nextGender });
                            // Clear incompatible product if gender no longer matches
                            const product =
                              selectedProduct ||
                              products.find((p) => p.productName === formData.loanInfo.loanProduct);
                            if (product && !isApplicantEligibleForProduct(nextGender, product)) {
                              setSelectedProduct(null);
                              setLoanInfo({ loanProduct: '', currency: '' as any });
                              setError(
                                productGenderRestrictionMessage(product) ||
                                  'Selected loan product is not available for the updated gender. Please choose another product.'
                              );
                            }
                          }}
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                        </Select>
                        {validationErrors['p0.gender'] && <FormHelperText>{validationErrors['p0.gender']}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                  <FormControl fullWidth required error={Boolean(validationErrors['p0.maritalStatus'])} data-field-key="p0.maritalStatus">
                        <InputLabel>Marital Status *</InputLabel>
                        <Select value={p0.maritalStatus} label="Marital Status *" onChange={(e: SelectChangeEvent<string>) => setPerson(0, { maritalStatus: (e.target.value as any) })}>
                          <MenuItem value="Divorced">Divorced</MenuItem>
                          <MenuItem value="Married">Married</MenuItem>
                          <MenuItem value="Single">Single</MenuItem>
                          <MenuItem value="Widowed">Widowed</MenuItem>
                    </Select>
                        {validationErrors['p0.maritalStatus'] && <FormHelperText>{validationErrors['p0.maritalStatus']}</FormHelperText>}
                  </FormControl>
                </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Nationality</InputLabel>
                        <Select
                          label="Nationality"
                          value={p0.nationality || ''}
                          onChange={(e) => setPerson(0, { nationality: e.target.value as string })}
                        >
                          <MenuItem value="">
                            <em>Select nationality</em>
                          </MenuItem>
                          {NATIONALITIES.map((nat) => (
                            <MenuItem key={nat} value={nat}>{nat}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Religion</InputLabel>
                        <Select
                          label="Religion"
                          value={p0.religion || ''}
                          onChange={(e) => setPerson(0, { religion: e.target.value as string })}
                        >
                          <MenuItem value="">Select religion</MenuItem>
                          <MenuItem value="Christianity">Christianity</MenuItem>
                          <MenuItem value="Hinduism">Hinduism</MenuItem>
                          <MenuItem value="Islam">Islam</MenuItem>
                          <MenuItem value="Judaism">Judaism</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                          <MenuItem value="Traditional">Traditional</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Disability" value={p0.disability} onChange={e => setPerson(0, { disability: e.target.value })} /></Grid>
                    
                    {/* Address Information */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Address Information</Typography></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Street Address" value={p0.addressDto.streetAddress} onChange={e => setPerson(0, { addressDto: { ...p0.addressDto, streetAddress: e.target.value } })} required {...fieldErrorProps('p0.address.streetAddress')} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth label="City" value={p0.addressDto.city} onChange={e => setPerson(0, { addressDto: { ...p0.addressDto, city: e.target.value } })} required {...fieldErrorProps('p0.address.city')} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth label="Country" value={p0.addressDto.country} onChange={e => setPerson(0, { addressDto: { ...p0.addressDto, country: e.target.value } })} required {...fieldErrorProps('p0.address.country')} /></Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth required error={Boolean(validationErrors['p0.address.state'])}>
                        <InputLabel>Province *</InputLabel>
                        <Select
                          label="Province *"
                          value={p0.addressDto.state || ''}
                          onChange={(e) => setPerson(0, { addressDto: { ...p0.addressDto, state: e.target.value as string } })}
                        >
                          <MenuItem value="">Select province</MenuItem>
                          <MenuItem value="Bulawayo">Bulawayo</MenuItem>
                          <MenuItem value="Harare">Harare</MenuItem>
                          <MenuItem value="Manicaland">Manicaland</MenuItem>
                          <MenuItem value="Mashonaland Central">Mashonaland Central</MenuItem>
                          <MenuItem value="Mashonaland East">Mashonaland East</MenuItem>
                          <MenuItem value="Mashonaland West">Mashonaland West</MenuItem>
                          <MenuItem value="Masvingo">Masvingo</MenuItem>
                          <MenuItem value="Matabeleland North">Matabeleland North</MenuItem>
                          <MenuItem value="Matabeleland South">Matabeleland South</MenuItem>
                          <MenuItem value="Midlands">Midlands</MenuItem>
                        </Select>
                        {validationErrors['p0.address.state'] && <FormHelperText>{validationErrors['p0.address.state']}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth label="Postal Code" value={p0.addressDto.postalCode} onChange={e => setPerson(0, { addressDto: { ...p0.addressDto, postalCode: e.target.value } })} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth label="Length of Stay" value={p0.addressDto.lengthOfStay} onChange={e => setPerson(0, { addressDto: { ...p0.addressDto, lengthOfStay: e.target.value } })} /></Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Ownership of Property</InputLabel>
                        <Select
                          value={p0.addressDto.ownershipOfProperty || ''}
                          label="Ownership of Property"
                          onChange={(e: SelectChangeEvent<string>) => setPerson(0, { addressDto: { ...p0.addressDto, ownershipOfProperty: e.target.value } })}
                        >
                          <MenuItem value="Employer Provided">Employer Provided</MenuItem>
                          <MenuItem value="Family">Family</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                          <MenuItem value="Owned">Owned</MenuItem>
                          <MenuItem value="Rented">Rented</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* Contact Details */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Contact Details</Typography></Grid>
                    <Grid item xs={12} md={4}><TextField fullWidth label="Phone Number" value={p0.contactDetails.phoneNumber} onChange={e => setPerson(0, { contactDetails: { ...p0.contactDetails, phoneNumber: e.target.value } })} required {...fieldErrorProps('p0.phoneNumber')} /></Grid>
                    <Grid item xs={12} md={4}><TextField fullWidth label="Email Address" value={p0.contactDetails.emailAddress} onChange={e => setPerson(0, { contactDetails: { ...p0.contactDetails, emailAddress: e.target.value } })} required {...fieldErrorProps('p0.emailAddress')} /></Grid>
                    <Grid item xs={12} md={4}><TextField fullWidth label="Alternative Phone" value={p0.contactDetails.alternativePhone} onChange={e => setPerson(0, { contactDetails: { ...p0.contactDetails, alternativePhone: e.target.value } })} /></Grid>
                    <Grid item xs={12} md={4}><TextField fullWidth label="Postal Address" value={p0.contactDetails.postalAddress} onChange={e => setPerson(0, { contactDetails: { ...p0.contactDetails, postalAddress: e.target.value } })} /></Grid>
                    <Grid item xs={12} md={4}><TextField fullWidth label="Home Phone" value={p0.contactDetails.homePhone} onChange={e => setPerson(0, { contactDetails: { ...p0.contactDetails, homePhone: e.target.value } })} /></Grid>
                    <Grid item xs={12} md={4}><TextField fullWidth label="Business Phone" value={p0.contactDetails.businessPhone} onChange={e => setPerson(0, { contactDetails: { ...p0.contactDetails, businessPhone: e.target.value } })} /></Grid>
                    
                    {/* Legal Declarations */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Legal Declarations</Typography></Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Has Criminal Records</InputLabel>
                        <Select value={p0.hasCriminalRecords ? 'Yes' : 'No'} label="Has Criminal Records" onChange={(e: SelectChangeEvent<string>) => setPerson(0, { hasCriminalRecords: e.target.value === 'Yes' })}>
                          <MenuItem value="No">No</MenuItem>
                          <MenuItem value="Yes">Yes</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Criminal Record Details" value={p0.criminalRecordDetails} onChange={e => setPerson(0, { criminalRecordDetails: e.target.value })} /></Grid>
                <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Has Civil Judgements</InputLabel>
                        <Select value={p0.hasCivilJudgements ? 'Yes' : 'No'} label="Has Civil Judgements" onChange={(e: SelectChangeEvent<string>) => setPerson(0, { hasCivilJudgements: e.target.value === 'Yes' })}>
                          <MenuItem value="No">No</MenuItem>
                          <MenuItem value="Yes">Yes</MenuItem>
                        </Select>
                      </FormControl>
                </Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Civil Judgement Details" value={p0.civilJudgementDetails} onChange={e => setPerson(0, { civilJudgementDetails: e.target.value })} /></Grid>
                
                    {/* Declaration */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Declaration & Terms and Conditions</Typography></Grid>
                    
                    {/* Terms and Conditions */}
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                          Terms and Conditions
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                          By submitting this loan application, I hereby declare and agree to the following terms and conditions:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontWeight: 500 }}>
                          1. Data Collection and Usage:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, ml: 2, lineHeight: 1.6 }}>
                          I consent to the collection, processing, and storage of my personal and business information for the purpose of evaluating my loan application. This information may be used for analytics, risk assessment, and compliance with regulatory requirements.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontWeight: 500 }}>
                          2. Third-Party Sharing:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, ml: 2, lineHeight: 1.6 }}>
                          I understand that my information may be shared with third-party service providers for background checks, credit verification, analytics, and other legitimate business purposes. This includes but is not limited to credit bureaus, identity verification services, and analytics partners.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontWeight: 500 }}>
                          3. Accuracy of Information:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, ml: 2, lineHeight: 1.6 }}>
                          I declare that all information provided in this application is true, accurate, and complete to the best of my knowledge. I understand that providing false or misleading information may result in the rejection of my application or legal consequences.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontWeight: 500 }}>
                          4. Consent for Communication:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, ml: 2, lineHeight: 1.6 }}>
                          I consent to receive communications regarding my loan application via phone, email, or SMS at the contact details provided.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6, fontStyle: 'italic', color: 'text.secondary' }}>
                          By checking the "I Accept" box below, I acknowledge that I have read, understood, and agree to be bound by these terms and conditions.
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, border: '1px dashed', borderColor: validationErrors['p0.declarationAccepted'] ? 'error.main' : 'grey.300', backgroundColor: '#fafafa' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Please confirm the Terms and Conditions</Typography>
                        <RadioGroup
                          row
                          name="declarationAccepted"
                          value={p0.declarationAccepted ? 'Yes' : 'No'}
                          onChange={(e) => setPerson(0, { declarationAccepted: e.target.value === 'Yes' })}
                        >
                          <FormControlLabel value="Yes" control={<Radio color="primary" />} label="I Accept" />
                          <FormControlLabel value="No" control={<Radio color="error" />} label="I Do Not Accept" />
                        </RadioGroup>
                        {validationErrors['p0.declarationAccepted'] && (
                          <FormHelperText error>{validationErrors['p0.declarationAccepted']}</FormHelperText>
                        )}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Name of Signatory" value={p0.declarationSignature} onChange={e => setPerson(0, { declarationSignature: e.target.value })} required {...fieldErrorProps('p0.declarationSignature')} /></Grid>
                    <Grid item xs={12} md={6}><TextField type="date" fullWidth label="Declaration Date" InputLabelProps={{ shrink: true }} value={p0.declarationDate} onChange={e => setPerson(0, { declarationDate: e.target.value })} required {...fieldErrorProps('p0.declarationDate')} /></Grid>
                    
                    {/* Qualifications */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Qualifications</Typography></Grid>
                    {p0.qualificationDto.map((qual, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Qualification {index + 1}</Typography>
                            <Tooltip title="Remove Qualification">
                              <IconButton color="error" onClick={() => {
                                const newQuals = [...p0.qualificationDto];
                                newQuals.splice(index, 1);
                                setPerson(0, { qualificationDto: newQuals });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Institution" value={qual.institution} onChange={e => {
                          const newQuals = [...p0.qualificationDto];
                          newQuals[index] = { ...newQuals[index], institution: e.target.value };
                          setPerson(0, { qualificationDto: newQuals });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Qualification" value={qual.qualification} onChange={e => {
                          const newQuals = [...p0.qualificationDto];
                          newQuals[index] = { ...newQuals[index], qualification: e.target.value };
                          setPerson(0, { qualificationDto: newQuals });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField type="date" fullWidth label="Start Date" InputLabelProps={{ shrink: true }} value={qual.startDate} onChange={e => {
                          const newQuals = [...p0.qualificationDto];
                          newQuals[index] = { ...newQuals[index], startDate: e.target.value };
                          setPerson(0, { qualificationDto: newQuals });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField type="date" fullWidth label="End Date" InputLabelProps={{ shrink: true }} value={qual.endDate} onChange={e => {
                          const newQuals = [...p0.qualificationDto];
                          newQuals[index] = { ...newQuals[index], endDate: e.target.value };
                          setPerson(0, { qualificationDto: newQuals });
                         }} /></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setPerson(0, { qualificationDto: [...p0.qualificationDto, { institution: '', qualification: '', startDate: '', endDate: '' }] })}>
                        Add Qualification
                      </Button>
                </Grid>
                
                    {/* Employment History */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Employment History</Typography></Grid>
                    {p0.employmentHistoryDto.map((emp, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Employment {index + 1}</Typography>
                            <Tooltip title="Remove Employment">
                              <IconButton color="error" onClick={() => {
                                const newEmps = [...p0.employmentHistoryDto];
                                newEmps.splice(index, 1);
                                setPerson(0, { employmentHistoryDto: newEmps });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Employer" value={emp.employer} onChange={e => {
                          const newEmps = [...p0.employmentHistoryDto];
                          newEmps[index] = { ...newEmps[index], employer: e.target.value };
                          setPerson(0, { employmentHistoryDto: newEmps });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Job Title" value={emp.jobTitle} onChange={e => {
                          const newEmps = [...p0.employmentHistoryDto];
                          newEmps[index] = { ...newEmps[index], jobTitle: e.target.value };
                          setPerson(0, { employmentHistoryDto: newEmps });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField type="date" fullWidth label="Start Date" InputLabelProps={{ shrink: true }} value={emp.startDate} onChange={e => {
                          const newEmps = [...p0.employmentHistoryDto];
                          newEmps[index] = { ...newEmps[index], startDate: e.target.value };
                          setPerson(0, { employmentHistoryDto: newEmps });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField type="date" fullWidth label="End Date" InputLabelProps={{ shrink: true }} value={emp.endDate} onChange={e => {
                          const newEmps = [...p0.employmentHistoryDto];
                          newEmps[index] = { ...newEmps[index], endDate: e.target.value };
                          setPerson(0, { employmentHistoryDto: newEmps });
                         }} /></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setPerson(0, { employmentHistoryDto: [...p0.employmentHistoryDto, { employer: '', jobTitle: '', startDate: '', endDate: '' }] })}>
                        Add Employment
                      </Button>
                </Grid>
                
                    {/* Personal Banking Information */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Personal Banking Information</Typography></Grid>
                    {p0.bankingInfoDto.map((bank, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Bank Account {index + 1}</Typography>
                            <Tooltip title="Remove Bank Account">
                              <IconButton color="error" onClick={() => {
                                const newBanks = [...p0.bankingInfoDto];
                                newBanks.splice(index, 1);
                                setPerson(0, { bankingInfoDto: newBanks });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Bank Name" value={bank.bankName} onChange={e => {
                          const newBanks = [...p0.bankingInfoDto];
                          newBanks[index] = { ...newBanks[index], bankName: e.target.value };
                          setPerson(0, { bankingInfoDto: newBanks });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Branch" value={bank.branch} onChange={e => {
                          const newBanks = [...p0.bankingInfoDto];
                          newBanks[index] = { ...newBanks[index], branch: e.target.value };
                          setPerson(0, { bankingInfoDto: newBanks });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Account Type" value={bank.accountType} onChange={e => {
                          const newBanks = [...p0.bankingInfoDto];
                          newBanks[index] = { ...newBanks[index], accountType: e.target.value };
                          setPerson(0, { bankingInfoDto: newBanks });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Account Number" value={bank.accountNumber} onChange={e => {
                          const newBanks = [...p0.bankingInfoDto];
                          newBanks[index] = { ...newBanks[index], accountNumber: e.target.value };
                          setPerson(0, { bankingInfoDto: newBanks });
                         }} /></Grid>
                      </React.Fragment>
                    ))}
                <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setPerson(0, { bankingInfoDto: [...p0.bankingInfoDto, { bankName: '', branch: '', accountType: '', accountNumber: '' }] })}>
                        Add Bank Account
                      </Button>
                </Grid>
                
                    {/* Next of Kin */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Next of Kin</Typography></Grid>
                    {p0.nextOfKinListDto.map((kin, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Next of Kin {index + 1}</Typography>
                            <Tooltip title="Remove Next of Kin">
                              <IconButton color="error" onClick={() => {
                                const newKins = [...p0.nextOfKinListDto];
                                newKins.splice(index, 1);
                                setPerson(0, { nextOfKinListDto: newKins });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Name" value={kin.name} onChange={e => {
                          const newKins = [...p0.nextOfKinListDto];
                          newKins[index] = { ...newKins[index], name: e.target.value };
                          setPerson(0, { nextOfKinListDto: newKins });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Phone" value={kin.phone} onChange={e => {
                          const newKins = [...p0.nextOfKinListDto];
                          newKins[index] = { ...newKins[index], phone: e.target.value };
                          setPerson(0, { nextOfKinListDto: newKins });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Relationship" value={kin.relationship} onChange={e => {
                          const newKins = [...p0.nextOfKinListDto];
                          newKins[index] = { ...newKins[index], relationship: e.target.value };
                          setPerson(0, { nextOfKinListDto: newKins });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Address" value={kin.address} onChange={e => {
                          const newKins = [...p0.nextOfKinListDto];
                          newKins[index] = { ...newKins[index], address: e.target.value };
                          setPerson(0, { nextOfKinListDto: newKins });
                         }} /></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setPerson(0, { nextOfKinListDto: [...p0.nextOfKinListDto, { name: '', phone: '', relationship: '', address: '' }] })}>
                        Add Next of Kin
                      </Button>
                </Grid>
                
                    {/* Personal Loans and Debts */}
                    <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Personal Loans and Debts</Typography></Grid>
                    {p0.loanOrDebtDto.map((debt, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Loan/Debt {index + 1}</Typography>
                            <Tooltip title="Remove Loan/Debt">
                              <IconButton color="error" onClick={() => {
                                const newDebts = [...p0.loanOrDebtDto];
                                newDebts.splice(index, 1);
                                setPerson(0, { loanOrDebtDto: newDebts });
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="To Whom" value={debt.toWhom} onChange={e => {
                          const newDebts = [...p0.loanOrDebtDto];
                          newDebts[index] = { ...newDebts[index], toWhom: e.target.value };
                          setPerson(0, { loanOrDebtDto: newDebts });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Amount Owed" type="number" value={debt.amountOwed} onChange={e => {
                          const newDebts = [...p0.loanOrDebtDto];
                          newDebts[index] = { ...newDebts[index], amountOwed: parseFloat(e.target.value) || 0 };
                          setPerson(0, { loanOrDebtDto: newDebts });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="purpose" value={debt.purpose} onChange={e => {
                          const newDebts = [...p0.loanOrDebtDto];
                          newDebts[index] = { ...newDebts[index], purpose: e.target.value };
                          setPerson(0, { loanOrDebtDto: newDebts });
                        }} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Monthly Repayment" type="number" value={debt.monthlyRepayment} onChange={e => {
                          const newDebts = [...p0.loanOrDebtDto];
                          newDebts[index] = { ...newDebts[index], monthlyRepayment: parseFloat(e.target.value) || 0 };
                          setPerson(0, { loanOrDebtDto: newDebts });
                         }} /></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={() => setPerson(0, { loanOrDebtDto: [...p0.loanOrDebtDto, { toWhom: '', amountOwed: 0, purpose: '', monthlyRepayment: 0 }] })}>
                        Add Loan/Debt
                      </Button>
                    </Grid>
                  </>
                )}

                {!isNeedsMoreInfoMode && activeStep === 2 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Loan Details</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required error={Boolean(validationErrors['l.loanProduct'])} data-field-key="l.loanProduct">
                        <InputLabel>
                          <LabelWithHelp text="Loan Product *" helpKey="loanProduct" />
                        </InputLabel>
                        <Select 
                          value={formData.loanInfo.loanProduct} 
                          label="Loan Product *" 
                          onFocus={() => setCurrentField('loanProduct')}
                          onChange={(e: SelectChangeEvent<string>) => {
                            const productName = e.target.value;
                            const product = products.find(p => p.productName === productName);
                            const applicantGender = formData.personalInfo[0]?.gender;
                            if (product && !isApplicantEligibleForProduct(applicantGender, product)) {
                              setError(
                                productGenderRestrictionMessage(product) ||
                                  'You are not eligible for this product based on gender'
                              );
                              return;
                            }
                            setSelectedProduct(product || null);
                            const currency = normalizeLoanCurrency(product?.currency);
                            const tenureOptions = getProductTenureOptions(product);
                            const currentTenure = formData.loanInfo.tenure;
                            const tenureStillValid = currentTenure && tenureOptions.includes(String(currentTenure));
                            setLoanInfo({
                              loanProduct: productName,
                              ...(currency ? { currency } : {}),
                              // Reset tenure if outside the product's allowed range
                              ...(!tenureStillValid
                                ? { tenure: (tenureOptions[tenureOptions.length - 1] || '') as any }
                                : {}),
                            });
                            setError(null);
                          }}
                          disabled={loadingProducts}
                        >
                          {loadingProducts ? (
                            <MenuItem value="" disabled>Loading products from Skyfin...</MenuItem>
                          ) : products.length === 0 ? (
                            <MenuItem value="" disabled>No Skyfin products available</MenuItem>
                          ) : filterProductsForApplicantGender(products, formData.personalInfo[0]?.gender).length === 0 ? (
                            <MenuItem value="" disabled>
                              {formData.personalInfo[0]?.gender
                                ? 'No products available for your gender'
                                : 'Select gender in Personal Info first'}
                            </MenuItem>
                          ) : (
                            filterProductsForApplicantGender(products, formData.personalInfo[0]?.gender).map((product) => {
                              const restriction = resolveProductEligibleGender(product);
                              return (
                                <MenuItem
                                  key={product.fineractProductId || product.id || product.productName}
                                  value={product.productName}
                                >
                                  {product.productName}
                                  {product.currency ? ` (${product.currency})` : ''}
                                  {restriction === 'Female' ? ' — Women only' : ''}
                                  {restriction === 'Male' ? ' — Men only' : ''}
                                </MenuItem>
                              );
                            })
                          )}
                        </Select>
                        {validationErrors['l.loanProduct'] ? (
                          <FormHelperText>{validationErrors['l.loanProduct']}</FormHelperText>
                        ) : formData.personalInfo[0]?.gender === 'Male' ? (
                          <FormHelperText>Women-only products are hidden for male applicants</FormHelperText>
                        ) : formData.personalInfo[0]?.gender === 'Female' ? (
                          <FormHelperText>Men-only products are hidden for female applicants</FormHelperText>
                        ) : (
                          <FormHelperText>Set your gender under Personal Info so products can be filtered</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required error={Boolean(validationErrors['l.currency'])}>
                        <InputLabel>
                          <LabelWithHelp text="Currency *" helpKey="currency" />
                        </InputLabel>
                        <Select 
                          value={formData.loanInfo.currency} 
                          label="Currency *" 
                          onChange={(e: SelectChangeEvent<string>) => setLoanInfo({ currency: (e.target.value as any) })}
                          disabled={Boolean(normalizeLoanCurrency(selectedProduct?.currency))}
                        >
                          <MenuItem value="USD">USD</MenuItem>
                          <MenuItem value="ZIG">ZIG</MenuItem>
                        </Select>
                        {validationErrors['l.currency'] ? (
                          <FormHelperText>{validationErrors['l.currency']}</FormHelperText>
                        ) : normalizeLoanCurrency(selectedProduct?.currency) ? (
                          <FormHelperText>
                            Auto-filled from product
                            {selectedProduct?.currency
                              ? ` (${selectedProduct.currency}${
                                  normalizeLoanCurrency(selectedProduct.currency) !== selectedProduct.currency.toUpperCase()
                                    ? ` → ${normalizeLoanCurrency(selectedProduct.currency)}`
                                    : ''
                                })`
                              : ''}
                          </FormHelperText>
                        ) : selectedProduct ? (
                          <FormHelperText>Select currency (not set on this product)</FormHelperText>
                        ) : null}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField 
                        fullWidth 
                        label={<LabelWithHelp text="Loan Amount" helpKey="loanAmount" />}
                        type="number" 
                        value={formData.loanInfo.loanAmount ?? ''} 
                        onChange={e => {
                          const value = e.target.value;
                          setLoanInfo({ loanAmount: value === '' ? undefined : parseFloat(value) });
                        }} 
                        onFocus={() => setCurrentField('loanAmount')}
                        required
                        placeholder="e.g., 5000, 10000, 25000"
                        inputProps={{ 'data-field-key': 'l.loanAmount' }}
                        error={
                          Boolean(validationErrors['l.loanAmount']) ||
                          (selectedProduct && formData.loanInfo.loanAmount !== undefined ? 
                          (formData.loanInfo.loanAmount > 0 && 
                          (
                            (selectedProduct.minimumPrincipal && formData.loanInfo.loanAmount < selectedProduct.minimumPrincipal) ||
                            (selectedProduct.maximumPrincipal && formData.loanInfo.loanAmount > selectedProduct.maximumPrincipal)
                          ) ? true : false) : false)
                        }
                        helperText={
                          validationErrors['l.loanAmount'] ||
                          (selectedProduct && formData.loanInfo.loanAmount !== undefined && formData.loanInfo.loanAmount > 0
                            ? (selectedProduct.minimumPrincipal && formData.loanInfo.loanAmount < selectedProduct.minimumPrincipal
                                ? `Minimum amount: ${selectedProduct.minimumPrincipal.toLocaleString()}`
                                : selectedProduct.maximumPrincipal && formData.loanInfo.loanAmount > selectedProduct.maximumPrincipal
                                ? `Maximum amount: ${selectedProduct.maximumPrincipal.toLocaleString()}`
                                : selectedProduct.minimumPrincipal && selectedProduct.maximumPrincipal
                                ? `Range: ${selectedProduct.minimumPrincipal.toLocaleString()} - ${selectedProduct.maximumPrincipal.toLocaleString()}`
                                : undefined)
                            : undefined)
                        }
                      />
                    </Grid>
                <Grid item xs={12} md={6}>
                      <FormControl fullWidth required error={Boolean(validationErrors['l.tenure'])} disabled={!selectedProduct}>
                        <InputLabel>
                          <LabelWithHelp text="Tenure (Months) *" helpKey="tenure" />
                        </InputLabel>
                        <Select 
                          value={
                            getProductTenureOptions(selectedProduct).includes(String(formData.loanInfo.tenure))
                              ? formData.loanInfo.tenure
                              : ''
                          } 
                          label="Tenure (Months) *" 
                          onFocus={() => setCurrentField('tenure')}
                          onChange={(e: SelectChangeEvent<string>) => setLoanInfo({ tenure: (e.target.value as any) })}
                          error={Boolean(validationErrors['l.tenure'])}
                        >
                          {getProductTenureOptions(selectedProduct).map((months) => (
                            <MenuItem key={months} value={months}>{months}</MenuItem>
                          ))}
                        </Select>
                        {validationErrors['l.tenure'] ? (
                          <FormHelperText error>{validationErrors['l.tenure']}</FormHelperText>
                        ) : selectedProduct ? (
                          <FormHelperText>
                            {selectedProduct.minLoanTenureMonths || selectedProduct.maxLoanTenureMonths || selectedProduct.loanTenureMonths
                              ? `Allowed for this product: ${getProductTenureOptions(selectedProduct).join(', ')} months`
                              : 'Select a tenure for this product'}
                          </FormHelperText>
                        ) : (
                          <FormHelperText>Select a loan product first</FormHelperText>
                        )}
                      </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                      <FormControl fullWidth required error={Boolean(validationErrors['l.branchName'])}>
                        <InputLabel>
                          <LabelWithHelp text="Branch *" helpKey="branchName" />
                        </InputLabel>
                        <Select
                          onFocus={() => setCurrentField('branchName')} 
                          value={formData.loanInfo.branchName || ''} 
                          label="Branch *" 
                          onChange={(e: SelectChangeEvent<string>) => {
                            const selectedBranch = e.target.value;
                            clearFieldErrors('l.branchName');
                            // Update both loanInfo.branchName and top-level branchName
                            setFormData(prev => ({
                              ...prev,
                              branchName: selectedBranch,
                              loanInfo: {
                                ...prev.loanInfo,
                                branchName: selectedBranch
                              }
                            }));
                          }}
                        >
                          <MenuItem value="">
                            <em>Select a branch</em>
                          </MenuItem>
                          {branches.map(b => (
                            <MenuItem key={b} value={b}>{b}</MenuItem>
                          ))}
                        </Select>
                        {validationErrors['l.branchName'] && (
                          <FormHelperText>{validationErrors['l.branchName']}</FormHelperText>
                        )}
                      </FormControl>
                </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={<LabelWithHelp text="Loan Purpose" helpKey="loanPurpose" />}
                        value={formData.loanInfo.loanPurpose}
                        onChange={e => setLoanInfo({ loanPurpose: e.target.value })}
                        onFocus={() => setCurrentField('loanPurpose')}
                        placeholder="e.g., Working capital for inventory, Purchase of equipment, Business expansion"
                        required
                        multiline
                        rows={2}
                        {...fieldErrorProps('l.loanPurpose')}
                      />
                    </Grid>
                    
                    {/* Cost Summary Section */}
                    {selectedProduct && costSummary && (
                      <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 3, mt: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                            Estimated Cost Summary
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            This is a rough estimate to help you plan. Actual costs may vary.
                          </Typography>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">Loan Amount</Typography>
                                <Typography variant="h6" color="primary">
                                  ${costSummary.loanAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">Loan Tenure</Typography>
                                <Typography variant="h6">
                                  {costSummary.tenureMonths} {costSummary.tenureMonths === 1 ? 'Month' : 'Months'}
                                </Typography>
                              </Box>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>Fees</Typography>
                            </Grid>
                            
                            {costSummary.applicationFee > 0 && (
                              <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2">Application Fee</Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    ${costSummary.applicationFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                            
                            {costSummary.administrationFees > 0 && (
                              <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2">Administration Fees</Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    ${costSummary.administrationFees.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                            
                            {costSummary.serviceFees > 0 && (
                              <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2">Service Fees</Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    ${costSummary.serviceFees.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                            
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" fontWeight={600}>Total Fees</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  ${costSummary.totalFees.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                            </Grid>
                            
                            {costSummary.totalInterest > 0 && (
                              <>
                                <Grid item xs={12}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>Interest</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">
                                      Total Interest ({costSummary.annualRate}% p.a. · {costSummary.interestMethodLabel})
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      ${costSummary.totalInterest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Calculated using this product&apos;s interest method over {costSummary.tenureMonths} months
                                  </Typography>
                                </Grid>
                              </>
                            )}
                            
                            <Grid item xs={12}>
                              <Box sx={{ mt: 2, pt: 2, borderTop: '2px solid', borderColor: 'primary.main' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="subtitle1" fontWeight={600}>Total Cost (Fees + Interest)</Typography>
                                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                                    ${costSummary.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="subtitle1" fontWeight={600}>Loan Repayment (Principal + Interest)</Typography>
                                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                                    ${costSummary.totalRepayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="h6" fontWeight={600}>Total Amount Payable</Typography>
                                  <Typography variant="h6" fontWeight={600} color="primary">
                                    ${(costSummary.totalAmountPayable ?? (costSummary.totalRepayment + costSummary.totalFees)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                                {costSummary.monthlyRepayment > 0 && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle2" color="text.secondary">Estimated Monthly Installment</Typography>
                                    <Typography variant="h6" fontWeight={600} color="success.main">
                                      ${costSummary.monthlyRepayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                            
                            {selectedProduct.minimumPrincipal && selectedProduct.maximumPrincipal && (
                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ mt: 2 }}>
                                  <Typography variant="caption">
                                    <strong>Principal Limits:</strong> Minimum ${selectedProduct.minimumPrincipal.toLocaleString()} - Maximum ${selectedProduct.maximumPrincipal.toLocaleString()}
                                  </Typography>
                                </Alert>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      </Grid>
                    )}
                  </>
                )}

                {activeStep === 3 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        {isNeedsMoreInfoMode ? 'Upload Required Documents' : 'Attachments'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {isNeedsMoreInfoMode 
                          ? 'Please upload the requested documents. All documents will be saved to the same location as your initial loan application.'
                          : 'Upload clear, legible documents. Supported: PDF, JPG, PNG.'}
                      </Typography>
                      {validationErrors['a.attachments'] && (
                        <Alert severity="error" sx={{ mb: 2 }} data-field-key="a.attachments">
                          {validationErrors['a.attachments']}
                        </Alert>
                      )}
                    </Grid>

                    {/* Single-file uploads */}
                    {LOAN_DOCUMENT_SLOTS.filter((item) => !item.multiple).map((item) => (
                      <Grid item xs={12} md={6} key={item.key}>
                        <Box sx={{
                          p: 2,
                          border: '1px dashed',
                          borderColor: validationErrors['a.attachments'] ? 'error.main' : 'grey.300',
                          borderRadius: 2,
                          bgcolor: validationErrors['a.attachments'] ? 'rgba(211, 47, 47, 0.04)' : 'grey.50',
                        }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>{item.label}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
                              Choose file
                              <input hidden type="file" onChange={e => {
                                clearFieldErrors('a.attachments');
                                setAttachments(p => ({ ...p, [item.key]: e.target.files?.[0] || null }));
                              }} />
                            </Button>
                            {(() => {
                              const f: any = (attachments as any)[item.key];
                              return f ? (
                                <Chip
                                  label={f.name || '1 file selected'}
                                  onDelete={() => removeSingleFile(item.key)}
                                  deleteIcon={<DeleteIcon />}
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">No file chosen</Typography>
                              );
                            })()}
                          </Box>
                        </Box>
                      </Grid>
                    ))}

                    {/* Multi-file uploads */}
                    {LOAN_DOCUMENT_SLOTS.filter((item) => item.multiple).map((item) => (
                      <Grid item xs={12} key={item.key}>
                        <Box sx={{
                          p: 2,
                          border: '1px dashed',
                          borderColor: validationErrors['a.attachments'] ? 'error.main' : 'grey.300',
                          borderRadius: 2,
                          bgcolor: validationErrors['a.attachments'] ? 'rgba(211, 47, 47, 0.04)' : 'grey.50',
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">{item.label} (multiple)</Typography>
                            {Array.isArray((attachments as any)[item.key]) && (attachments as any)[item.key].length > 0 && (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => clearAllFiles(item.key)}
                                sx={{ textTransform: 'none' }}
                              >
                                Clear All
                              </Button>
                            )}
                          </Box>
                          <Button variant="outlined" component="label" sx={{ textTransform: 'none', mb: 1 }}>
                            Choose files
                            <input hidden multiple type="file" onChange={e => {
                              clearFieldErrors('a.attachments');
                              setAttachments(p => ({ ...p, [item.key]: e.target.files ? Array.from(e.target.files) : [] }));
                            }} />
                          </Button>
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Array.isArray((attachments as any)[item.key]) && (attachments as any)[item.key].length > 0 ? (
                              (attachments as any)[item.key].map((f: File, idx: number) => (
                                <Chip
                                  key={idx}
                                  label={f.name}
                                  onDelete={() => removeMultiFile(item.key, idx)}
                                  deleteIcon={<DeleteIcon />}
                                  color="primary"
                                  variant="outlined"
                                />
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary">No files chosen</Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </>
                )}

                {!isNeedsMoreInfoMode && activeStep === 4 && (
                  <>
                    <Grid item xs={12}><Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Review</Typography></Grid>
                    {/* Business Info Card */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Business Information</Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6}><Labeled label="Applicant" value={formData.businessInfoDto.nameOfApplicant || '-'} /></Grid>
                          <Grid item xs={12} md={6}><Labeled label="Business Name" value={formData.businessInfoDto.nameOfBusiness || '-'} /></Grid>
                          <Grid item xs={12} md={6}><Labeled label="Ownership" value={formData.businessInfoDto.formOfBusinessOwnership || '-'} /></Grid>
                          <Grid item xs={12} md={6}><Labeled label="Status" value={formData.businessInfoDto.statusOfBusiness || '-'} /></Grid>
                          {formData.businessInfoDto.tradeReferenceDto.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Trade References</Typography>
                              <TableContainer component={Paper} sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Trade Name</TableCell>
                                      <TableCell>Account Number</TableCell>
                                      <TableCell>Phone</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {formData.businessInfoDto.tradeReferenceDto.map((t, i) => (
                                      <TableRow key={i}>
                                        <TableCell>{t.tradeName || '-'}</TableCell>
                                        <TableCell>{t.accountNumber || '-'}</TableCell>
                                        <TableCell>{t.phoneNumber || '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          )}
                          {formData.businessInfoDto.bankingInfoDto.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Business Bank Accounts</Typography>
                              <TableContainer component={Paper} sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Bank</TableCell>
                                      <TableCell>Branch</TableCell>
                                      <TableCell>Type</TableCell>
                                      <TableCell>Account</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {formData.businessInfoDto.bankingInfoDto.map((b, i) => (
                                      <TableRow key={i}>
                                        <TableCell>{b.bankName || '-'}</TableCell>
                                        <TableCell>{b.branch || '-'}</TableCell>
                                        <TableCell>{b.accountType || '-'}</TableCell>
                                        <TableCell>{b.accountNumber || '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          )}
                          {formData.businessInfoDto.loanOrDebtDto.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Business Loans/Debts</Typography>
                              <TableContainer component={Paper} sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Creditor</TableCell>
                                      <TableCell>Purpose</TableCell>
                                      <TableCell>Amount Owed</TableCell>
                                      <TableCell>Monthly Repayment</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {formData.businessInfoDto.loanOrDebtDto.map((d, i) => (
                                      <TableRow key={i}>
                                        <TableCell>{d.toWhom || '-'}</TableCell>
                                        <TableCell>{d.purpose || '-'}</TableCell>
                                        <TableCell>{d.amountOwed || 0}</TableCell>
                                        <TableCell>{d.monthlyRepayment || 0}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          )}
                          {formData.businessInfoDto.otherBusinesses.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Other Businesses</Typography>
                              <TableContainer component={Paper} sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Nature</TableCell>
                                      <TableCell>Location</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {formData.businessInfoDto.otherBusinesses.map((o, i) => (
                                      <TableRow key={i}>
                                        <TableCell>{o.nameOfBusiness || '-'}</TableCell>
                                        <TableCell>{o.natureOfOperation || '-'}</TableCell>
                                        <TableCell>{o.location || '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Loan Info Card */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Loan Details</Typography>
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, width: 180 }}>Product</TableCell>
                                <TableCell>{formData.loanInfo.loanProduct || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                                <TableCell>{formData.loanInfo.currency || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Tenure (months)</TableCell>
                                <TableCell>{formData.loanInfo.tenure || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                <TableCell>{formData.loanInfo.loanAmount ?? '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                                <TableCell>{formData.loanInfo.branchName || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                                <TableCell>{formData.loanInfo.loanPurpose || '-'}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Grid>

                    {/* Applicants Card */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Applicants</Typography>
                        {formData.personalInfo.map((p, idx) => (
                          <Box key={idx} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Applicant {idx + 1}</Typography>
                            <TableContainer component={Paper}>
                              <Table size="small">
                                <TableBody>
                                  <TableRow><TableCell sx={{ width: 200, fontWeight: 600 }}>First Name</TableCell><TableCell>{p.firstName || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Last Name</TableCell><TableCell>{p.lastName || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>ID Number</TableCell><TableCell>{p.idNumber || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Gender</TableCell><TableCell>{p.gender || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>DOB</TableCell><TableCell>{p.dateOfBirth || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Marital Status</TableCell><TableCell>{p.maritalStatus || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Nationality</TableCell><TableCell>{p.nationality || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>City</TableCell><TableCell>{p.addressDto?.city || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Country</TableCell><TableCell>{p.addressDto?.country || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Street</TableCell><TableCell>{p.addressDto?.streetAddress || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Phone</TableCell><TableCell>{p.contactDetails?.phoneNumber || '-'}</TableCell></TableRow>
                                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Email</TableCell><TableCell>{p.contactDetails?.emailAddress || '-'}</TableCell></TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {p.qualificationDto?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">Qualifications</Typography>
                                <TableContainer component={Paper} sx={{ mt: 1 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Institution</TableCell>
                                        <TableCell>Qualification</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>End Date</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {p.qualificationDto.map((q, i) => (
                                        <TableRow key={i}>
                                          <TableCell>{q.institution || '-'}</TableCell>
                                          <TableCell>{q.qualification || '-'}</TableCell>
                                          <TableCell>{q.startDate || '-'}</TableCell>
                                          <TableCell>{q.endDate || '-'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                            {p.employmentHistoryDto?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">Employment History</Typography>
                                <TableContainer component={Paper} sx={{ mt: 1 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Employer</TableCell>
                                        <TableCell>Job Title</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>End Date</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {p.employmentHistoryDto.map((e, i) => (
                                        <TableRow key={i}>
                                          <TableCell>{e.employer || '-'}</TableCell>
                                          <TableCell>{e.jobTitle || '-'}</TableCell>
                                          <TableCell>{e.startDate || '-'}</TableCell>
                                          <TableCell>{e.endDate || '-'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                            {p.bankingInfoDto?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">Personal Bank Accounts</Typography>
                                <TableContainer component={Paper} sx={{ mt: 1 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Bank</TableCell>
                                        <TableCell>Branch</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Account</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {p.bankingInfoDto.map((b, i) => (
                                        <TableRow key={i}>
                                          <TableCell>{b.bankName || '-'}</TableCell>
                                          <TableCell>{b.branch || '-'}</TableCell>
                                          <TableCell>{b.accountType || '-'}</TableCell>
                                          <TableCell>{b.accountNumber || '-'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                            {p.loanOrDebtDto?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">Personal Loans/Debts</Typography>
                                <TableContainer component={Paper} sx={{ mt: 1 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Creditor</TableCell>
                                        <TableCell>Purpose</TableCell>
                                        <TableCell>Amount Owed</TableCell>
                                        <TableCell>Monthly Repayment</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {p.loanOrDebtDto.map((d, i) => (
                                        <TableRow key={i}>
                                          <TableCell>{d.toWhom || '-'}</TableCell>
                                          <TableCell>{d.purpose || '-'}</TableCell>
                                          <TableCell>{d.amountOwed || 0}</TableCell>
                                          <TableCell>{d.monthlyRepayment || 0}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                            {p.nextOfKinListDto?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">Next of Kin</Typography>
                                <TableContainer component={Paper} sx={{ mt: 1 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Relationship</TableCell>
                                        <TableCell>Address</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {p.nextOfKinListDto.map((n, i) => (
                                        <TableRow key={i}>
                                          <TableCell>{n.name || '-'}</TableCell>
                                          <TableCell>{n.phone || '-'}</TableCell>
                                          <TableCell>{n.relationship || '-'}</TableCell>
                                          <TableCell>{n.address || '-'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">Declarations</Typography>
                              <TableContainer component={Paper} sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableBody>
                                    <TableRow>
                                      <TableCell sx={{ width: 220, fontWeight: 600 }}>Criminal Records</TableCell>
                                      <TableCell>{p.hasCriminalRecords ? 'Yes' : 'No'}{p.criminalRecordDetails ? ` - ${p.criminalRecordDetails}` : ''}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Civil Judgements</TableCell>
                                      <TableCell>{p.hasCivilJudgements ? 'Yes' : 'No'}{p.civilJudgementDetails ? ` - ${p.civilJudgementDetails}` : ''}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Declaration Accepted</TableCell>
                                      <TableCell>{p.declarationAccepted ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Declaration Date</TableCell>
                                      <TableCell>{p.declarationDate || '-'}</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Grid>

                    {/* Attachments Card */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Attachments</Typography>
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: 320, fontWeight: 600 }}>Document</TableCell>
                                <TableCell>Files</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {LOAN_DOCUMENT_SLOTS.map((slot) => {
                                const val: any = (attachments as any)[slot.key];
                                let content: React.ReactNode = 'None';
                                if (Array.isArray(val)) {
                                  content = val.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {val.map((f: File, idx: number) => (
                                        <FileChip key={idx} file={f} />
                                      ))}
                                    </Box>
                                  ) : 'None';
                                } else if (val) {
                                  const f = val as File;
                                  content = f?.name ? (
                                    <FileChip file={f} />
                                  ) : '1 file';
                                }
                                return (
                                  <TableRow key={slot.key}>
                                    <TableCell>{slot.multiple ? `${slot.label} (multiple)` : slot.label}</TableCell>
                                    <TableCell>{content}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Grid>

                    {/* Submission Card */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Submission</Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">Platform</Typography><Typography variant="body1">{formData.platformUsed}</Typography></Grid>
                          <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">Comment</Typography><Typography variant="body1">{formData.comment}</Typography></Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </form>
            {showValidationSummary && Object.keys(validationErrors).length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setShowValidationSummary(false)}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Fix the highlighted fields before continuing
                </Typography>
                {stepErrorFlags.map((flag, idx) => flag && (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setActiveStep(idx)}
                      sx={{ textTransform: 'none', fontWeight: 700, px: 0, minWidth: 0 }}
                    >
                      {steps[idx]} ({stepErrorCounts[idx]} issue{stepErrorCounts[idx] === 1 ? '' : 's'})
                    </Button>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {Object.entries(validationErrors)
                        .filter(([key]) => {
                          if (idx === 0) return key.startsWith('b.');
                          if (idx === 1) return key.startsWith('p0.');
                          if (idx === 2) return key.startsWith('l.');
                          if (idx === 3) return key.startsWith('a.');
                          return false;
                        })
                        .map(([key, msg]) => (
                          <li key={key}><Typography variant="body2">{msg}</Typography></li>
                        ))}
                    </Box>
                  </Box>
                ))}
              </Alert>
            )}
            {error && (<Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>)}
          </Box>
        )}
      </DialogContent>
      {!success && (
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClose} disabled={loading} color="error" variant="contained">Cancel</Button>
          {!isNeedsMoreInfoMode && (
            <>
              <Button onClick={() => setActiveStep(s => Math.max(0, s - 1))} disabled={loading || activeStep === 0}>Back</Button>
              {activeStep < steps.length - 1 ? (
                <Button onClick={handleNext} variant="contained" disabled={loading}>Next</Button>
              ) : (
                <Button onClick={handleSubmit} variant="contained" disabled={loading} sx={{ minWidth: 120 }}>{loading ? <CircularProgress size={24} /> : (isUpdateMode ? 'Update Application' : 'Submit Application')}</Button>
              )}
            </>
          )}
          {isNeedsMoreInfoMode && (
            <Button onClick={handleSubmit} variant="contained" disabled={loading} sx={{ minWidth: 120 }}>
              {loading ? <CircularProgress size={24} /> : 'Upload Documents'}
            </Button>
          )}
        </DialogActions>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)}>
        <DialogTitle>Cancel Application?</DialogTitle>
        <DialogContent>
          <Typography>
            You have progress on this form. Do you want to save a draft before exiting?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancelOpen(false)}>Continue Editing</Button>
          <Button color="error" variant="contained" onClick={async () => {
            // Only save draft for NEW applications, not updates
            if (!isUpdateMode) {
              try {
                formDraftService.saveDraftLocally(formData, activeStep);
                await formDraftService.saveDraftToServer(formData, activeStep);
              } catch {}
            }
            setConfirmCancelOpen(false);
            onClose();
          }}>Save Draft & Exit</Button>
          <Button variant="outlined" onClick={() => { setConfirmCancelOpen(false); onClose(); }}>Exit Without Saving</Button>
        </DialogActions>
      </Dialog>

      {/* Draft Restoration Dialog */}
      <Dialog open={showDraftRestore} maxWidth="sm" fullWidth>
        <DialogTitle>Restore Previous Draft</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            We found a previously saved draft of your loan application from{' '}
            {draftInfo?.lastSaved ? new Date(draftInfo.lastSaved).toLocaleString() : 'an earlier session'}.
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You were on step {draftInfo?.step ? draftInfo.step + 1 : 1} of {steps.length}.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Would you like to restore this draft and continue from where you left off?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardDraft} color="error">
            Discard Draft
          </Button>
          <Button onClick={handleRestoreDraft} variant="contained" autoFocus>
            Restore Draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session Timeout Warning */}
      <SessionTimeoutWarning
        open={showWarning}
        timeLeft={timeLeft}
        onExtendSession={extendSession}
        onSaveAndLogout={() => saveAndLogout(formData, activeStep)}
        onLogout={forceLogout}
      />

      {/* Auto-save notifications removed for silent UX */}

      {/* Manual Save button removed; drafts save automatically */}
    </Dialog>
  );
};

export default LoanApplicationModal;



